/**
 * Hand-typed Deepkit API client for the Cotera frontend.
 *
 * - No dependency on @cotera/contracts or TenantedContract routing machinery.
 * - All endpoint params / outputs are defined as plain TypeScript interfaces.
 * - Result<T, E> is from neverthrow; 2xx → ok(data), 4xx/5xx → err(data).
 */

import axios, { type AxiosError } from 'axios';
import { ok, err, Result } from 'neverthrow';
import { clearBearerTokenCache } from '../../hooks/bearer-token-cache';
import type { Ty, AST } from '@cotera/era';
import {
  API_SCHEMA_HEADER,
  AgentChatBatchV20260601Schema,
  AgentChatV20260601Schema,
  AgentCreateV20260601Schema,
  AgentUpdateV20260601Schema,
  ArtifactsCreateV20260601Schema,
  AtcRunColumnValueV20260601Schema,
  BillingAddCardSessionV20260601Schema,
  BillingAddCreditsV20260601Schema,
  BillingTopUpV20260601Schema,
  BillingUpdateCardSessionV20260601Schema,
  ChatActionsV20260601Schema,
  ChatCompactV20260601Schema,
  ChatMessagesReadV20260601Schema,
  ChatMessagesUnreadV20260601Schema,
  ChatRenameV20260601Schema,
  ChatStepV20260601Schema,
  ChatPostV20260601Schema,
  ChatAddInterruptableToolsV20260601Schema,
  ChatToolRunInterruptedV20260601Schema,
  ChatToolRunV20260601Schema,
  ChatToolSubmitV20260601Schema,
  CommitsCommitV20260601Schema,
  DatasetColumnSettingsV20260601Schema,
  DatasetCsvCreatePendingV20260601Schema,
  DatasetCsvCreateV20260601Schema,
  DatasetManualCreateV20260601Schema,
  DatasetPromoteV20260601Schema,
  DatasetWarehouseCreateV20260601Schema,
  DatasetWarehouseReseedV20260601Schema,
  DatasetOperationsBatchV20260601Schema,
  DatasetRowKeyCheckV20260601Schema,
  DatasetSettingsV20260601Schema,
  DocumentCreateV20260601Schema,
  DocumentUpdateV20260601Schema,
  FolderCreateV20260601Schema,
  FolderItemsV20260601Schema,
  FolderLibraryRootV20260601Schema,
  CliAuthApproveV20260601Schema,
  FolderMoveV20260601Schema,
  FolderRenameV20260601Schema,
  OnboardingOrgSetupV20260601Schema,
  OnboardingReferralV20260601Schema,
  ResourceActivateShareLinkV20260601Schema,
  ResourceCreateShareLinkV20260601Schema,
  ResourceGetAccessV20260925Schema,
  ResourceListActiveShareLinksV20260925Schema,
  ResourcePreviewShareLinkV20260925Schema,
  ResourceRemoveSharedWithOrganizationV20260925Schema,
  ResourceSetFollowsFolderSharingV20260928Schema,
  ResourceRemoveUserPermissionV20260925Schema,
  ResourceSetUserPermissionV20260925Schema,
  ResourceInviteByEmailV20260928Schema,
  ResourceListV20260601Schema,
  ResourceListFavoritesV20260601Schema,
  ResourceReorderFavoritesV20260601Schema,
  ResourceSetEmojiV20260601Schema,
  ResourceSetFavoriteV20260601Schema,
  ResourceSetSharedWithOrganizationV20260601Schema,
  ToolsExecuteV20260601Schema,
  ToolsUpdateToolsConnectionV20260601Schema,
  ToolsV2CreateAccountV20260601Schema,
  WarehouseConnectionCreateTableV20260601Schema,
  WarehouseConnectionCreateV20260601Schema,
  WarehouseRunMaterializationV20260601Schema,
  WarehouseSnapshotEventStreamV20260601Schema,
  WorkflowAddTriggerV20260601Schema,
  WorkflowBindTriggerV20260601Schema,
  WorkflowCreateV20260601Schema,
  WorkflowDraftTestRunV20260601Schema,
  WorkflowEventsV20260601Schema,
  WorkflowTriggerNowV20260601Schema,
  WorkflowListRunsV20260601Schema,
  WorkflowRunStatsV20260601Schema,
  WorkflowScheduledAgentUpdateV20260601Schema,
  WorkflowSummariesV20260601Schema,
  WorkflowRunsByWorkflowsV20260601Schema,
  WorkflowTriggerSummariesV20260601Schema,
  WorkflowTriggerV20260601Schema,
  WorkflowUpdateTriggerV20260601Schema,
  WorkflowRenameTriggerV20260601Schema,
  WorkflowUpdateV20260601Schema,
  WorkflowCancelRunV20260601Schema,
  WorkflowRetryRunV20260601Schema,
  WorkflowClearDebounceV20260601Schema,
  WorkflowResumeStepV20260601Schema,
  type WorkflowCancelRunResponse,
  type WorkflowRetryRunResponse,
  type WorkflowClearDebounceResponse,
  type WorkflowResumeStepResponse,
  type ChatInsightsResponse,
  type ChatAddInterruptableToolsResponse,
  type ChatPostBodyV20260601,
  type ChatPostInterruptableTool,
  type ChatPostResponse,
  type ChatSuccessResponse,
  type ChatToolRunResponse,
  type ChatToolSubmitResponse,
  type ChatTriggerSource,
  type DatasetColumnSettingsBodyV20260601,
  type DatasetManualCreateBody,
  type DatasetWarehouseCreateBody,
  type DatasetWarehouseReseedBody,
  type DatasetOperationBatchBodyV20260601,
  type DatasetRowKeyCheckBodyV20260601,
  type DatasetRowKeyCheckResponseV20260601,
  type DatasetSettingsBodyV20260601,
  type DatasetOperationBatchResponseV20260601,
  type DatasetOperationListResponseV20260601,
  type WorkflowAddTriggerBody,
  type WorkflowCreateBody,
  type WorkflowTriggerNowBody,
  type WorkflowUpdateBody,
} from '@cotera/api';
import type {
  ResourceAccessResponse,
  ResourceShareLinkPreviewResponse,
} from '@cotera/api';
import type {
  DatasetRunGroupProgress,
  DatasetTriggerCriterion,
  DatasetTriggerRunMode,
  WebhookProviderKey,
} from '@cotera/api';
// The canonical enum, aliased so it doesn't shadow this file's own string-union
// `ResourceType`. Organize members carry the enum, matching MovableResource and
// the rest of the app (enum values assign to the string-union params here).
import type { ResourceType as ApiResourceType } from '@cotera/api';

// ---------------------------------------------------------------------------
// Shared error type
// ---------------------------------------------------------------------------

export interface ApiErrBody {
  errorType: string;
  message?: string;
  /**
   * Structured per-item failures carried by errors like BuildError. Preserved
   * verbatim from the server so callers can surface the real detail (e.g. which
   * symbol failed) instead of a generic message.
   */
  errors?: { error: { t: string; message?: string } }[];
  /** Machine-readable reason, where an endpoint gives one (e.g. why a share link can't be opened). */
  code?: string;
}

// Match the old contract client error envelope so existing FE code needs no changes.
export type ApiErr =
  | { t: 'server'; err: ApiErrBody }
  | { t: 'application'; err: ApiErrBody }
  | { t: 'client'; err: ApiErrBody };

// ---------------------------------------------------------------------------
// Shared / domain-lite types (inlined to avoid contract imports)
// ---------------------------------------------------------------------------

// --- Whoami ---
export interface WhoamiOrg {
  id: string;
  name: string;
  slug: string;
  workosId: string | null;
}

export type Org = WhoamiOrg;

export interface WhoamiTenantedRole {
  orgId: string;
  role: 'ADMIN' | 'ARTIFACTUSER' | 'DEMO' | 'DEVELOPER' | 'VIEWER' | 'BOT';
}

export interface WhoamiImpersonator {
  id: string;
  name: string | null;
  email: string;
}

export interface Whoami {
  id: string;
  name: string | null;
  email: string;
  onboardingIncomplete: boolean | null;
  impersonator: WhoamiImpersonator | null;
  orgs: WhoamiOrg[];
  tenantedRoles: WhoamiTenantedRole[];
  untenantedRoles: ('SUPERADMIN' | 'SYSTEM')[];
  pylonVerificationHash: string | null;
}

// --- Commits ---
export type ColWhen = 'hourly' | 'daily' | 'never' | { cron: string };

export type RelColConfig =
  | { t: 'eraql'; source: string }
  | {
      t: 'llm';
      version?: 'v1' | 'v2';
      outputTy: string;
      editorState?: unknown;
      model?: string;
      prompt?: string;
      systemMessage?: string;
      imageUrl?: string | null;
      when?: ColWhen;
      temperature?: number;
      maxOutputTokens?: number;
      topP?: number;
      topK?: number;
      frequencyPenalty?: number;
      presencePenalty?: number;
      tools?: string[];
      durable?: boolean;
      schedule?: ColWhen;
    }
  | {
      t: 'workflow';
      source: string;
      toolSymbolMap: Record<string, { toolName: string; toolId: string }>;
      when: ColWhen;
    };

export type CommitOperation =
  | { t: 'create-column'; relName: string; name: string; config: RelColConfig }
  | {
      t: 'create-expr';
      symbol: string;
      source: string;
      metadata?: Record<string, unknown>;
    }
  | {
      t: 'create-document';
      symbol: string;
      source: unknown;
      metadata?: Record<string, unknown>;
    }
  | {
      t: 'create-agent';
      symbol: string;
      outputTy: string;
      editorState: unknown;
      model: string;
      schedule?: ColWhen;
      temperature?: number;
      maxOutputTokens?: number;
      topP?: number;
      topK?: number;
      frequencyPenalty?: number;
      presencePenalty?: number;
    }
  | {
      t: 'create-rel';
      symbol: string;
      source: string;
      where?: string | null;
      pk?: string | null;
    }
  | {
      t: 'create-tool';
      symbol: string;
      def: { type: string; t: 'ffi'; toolId: string; toolConnectionId: string };
    }
  | {
      t: 'update-expr';
      symbol: string;
      source?: string;
      metadata?: Record<string, unknown>;
    }
  | {
      t: 'update-document';
      symbol: string;
      source?: unknown;
      metadata?: Record<string, unknown>;
    }
  | {
      t: 'update-agent';
      symbol: string;
      outputTy?: string;
      editorState?: unknown;
      model?: string;
      schedule?: ColWhen;
    }
  | {
      t: 'update-rel';
      symbol: string;
      source?: string;
      pk?: string | null;
      where?: string | null;
    }
  | {
      t: 'update-column' | 'replace-column';
      relName: string;
      name: string;
      config: RelColConfig;
    }
  | {
      t: 'update-tool';
      symbol: string;
      def: { type: string; t: 'ffi'; toolId: string; toolConnectionId: string };
    }
  | { t: 'delete'; symbol: string }
  | { t: 'delete-col'; relName: string; name: string }
  | { t: 'rename'; symbol: string; newSymbol: string }
  | { t: 'rename-col'; relName: string; name: string; newName: string }
  | { t: 'lock'; symbol: string }
  | { t: 'unlock'; symbol: string }
  | {
      t: 'duplicate';
      symbol?: string;
      newSymbol: string;
      relName?: string;
      name?: string;
      newName?: string;
    };

export interface Commit {
  message: string | null;
  operations: CommitOperation[];
  actorId: string;
  timestamp: string | Date;
  optimisticIndex?: number;
}

export interface SavedCommit extends Commit {
  index: number;
}

// --- Artifacts ---
export interface Artifact {
  id: string;
  status: 'ready' | 'pending' | { status: 'failed'; message: string };
}

export interface ArtifactFullfillment {
  id: string;
  orgId: string;
  numberOfRows: number;
  enforcedLimit: number | null;
  signature: Record<string, unknown>;
  createdAt: string | Date;
}

export interface ArtifactFailureInfo {
  errorMessage: string;
  createdAt: string | Date;
}

export interface ArtifactRequest {
  id: string;
  sqlHash: string;
  updatedAt: string | Date;
  createdAt: string | Date;
  requestedByUserId: string;
  fullfillment: ArtifactFullfillment | null;
  failure: ArtifactFailureInfo | null;
  invalidatedAt: string | Date | null;
  lastHeartBeatAt: string | Date | null;
}

export type UsableCacheEntryStatus =
  | { t: 'pending' }
  | { t: 'ready'; signature: Record<string, unknown>; complete: boolean };

export interface UsableCacheEntry {
  id: string;
  createdAt: string | Date;
  status: UsableCacheEntryStatus;
}

// --- Tools ---
export interface ToolItem {
  id: string;
  name: string;
  displayName: string;
  toolConnectionId: string;
}

export interface ToolConnection {
  id: string;
  type: 'apiKey' | 'oauth' | 'password' | 'noAuth';
  provider: string;
  displayName: string;
  enabled: boolean;
  createdAt: Date | string;
}

export interface ToolSecret {
  apiKey?: string;
  [key: string]: unknown;
}

// --- Warehouse ---
export interface PrimaryWarehouse {
  displayName: string;
  type: string;
  readSchema: string;
  writeSchema: string;
}

export type StreamSpecifier =
  | { t: 'manifest'; id: string; entityName: string }
  | {
      t: 'graph';
      relIdentity: [number, number];
      colIdentity: [number, number];
    };

// --- ATC ---
export type ATCStackFrameStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'waiting'
  | 'queued'
  | 'allocated'
  | 'stalled';

export interface StackFrameEvent {
  id: string;
  eventType:
    | 'start'
    | 'success'
    | 'fail'
    | 'error'
    | 'retry'
    | 'cancel'
    | 'queued'
    | 'allocated';
  createdAt: Date;
  metadataId: string | null;
}

export interface StackFrame {
  id: string;
  parentId: string | null;
  status: ATCStackFrameStatus;
  createdAt: Date;
  depth: number;
  position: number;
  input: unknown | null;
  output: Record<string, unknown> | null;
  fpType: string;
  displayName: string;
  tags: string[];
  retries: number;
  events: StackFrameEvent[];
  startedAt: Date | null;
  endedAt: Date | null;
  allocatedAt: Date | null;
  cancelledAt: Date | null;
}

export interface GetStackFramesForRunKeyResponse {
  runId: string;
  orgId: string;
  runKey: string;
  tags: string[];
  heartbeatAt: Date | null;
  uddWriteKey: unknown;
  frames: StackFrame[];
}

export type StackFrameType = StackFrame;

export interface StackFramePreview {
  id: string;
  status: ATCStackFrameStatus;
  depth: number;
  position: number;
  output: Record<string, unknown> | null;
  fpType: string;
  displayName: string;
}

export interface GetStackFramesForRunKeyPreviewResponse {
  frame: StackFramePreview | null;
  allComplete: boolean;
}

export interface RunGroupRun {
  type: 'run';
  runKey: string;
  frameKey: string;
  id: string;
  status: 'pending' | 'completed' | 'failed';
  result: unknown;
  startAt: Date | string | null;
  endAt: Date | string | null;
}

export interface RunGroupGroup {
  type: 'group';
  runKey: string;
  status: 'pending' | 'completed' | 'failed' | 'none';
  stats: {
    startAt: Date | string | null;
    endAt: Date | string | null;
    total: number;
    pending: number;
    completed: number;
    failed: number;
  };
}

export type RunOrGroupStats = RunGroupRun | RunGroupGroup;

export interface AllocatorLimit {
  resource: string;
  amount: number;
  period: string;
  periodStart: Date | string;
  tag: string;
}

export interface RemainingRateLimit {
  resource: string;
  remaining: number;
  period: string;
}

// --- Org ---
export interface PlanRateLimit {
  id: string;
  resource: string;
  amount: number;
  period: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface OrgPlan {
  id: string;
  name: string;
  billingPlanName: string;
  description: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  rateLimits: PlanRateLimit[];
}

export interface OrgMember {
  id: string;
  name: string | null;
  email: string;
}

// --- Billing ---
export type BalanceInfo =
  | {
      kind: 'orb-paygo-business';
      currentCreditBalance: number;
      canAddCC: boolean;
    }
  | {
      kind: 'orb-enterprise';
      balance: {
        creditsCredits:
          | { kind: 'positive'; credits: number }
          | {
              kind: 'negative';
              creditsOverageAmount: number;
              dollarsToBeCharged: number;
            };
        engineeringHours: number;
      };
    };

export interface OrbEnterpriseInfo {
  invoicesPortalUrl: string;
  balance: {
    credits: number;
    engineeringHours: number;
  };
}

export type OrbCreditCardInfo =
  | {
      kind: 'saved-card';
      savedCard: { kind: string; lastFour: string; expiration: string };
    }
  | { kind: 'no-saved-card' };

export type TopUpConfig = { amount: number; threshold: number } | null;

export interface OrbInfo {
  invoicesPortalUrl: string;
  creditsBalance: number;
  creditCardInfo: OrbCreditCardInfo;
  topUpConfig: TopUpConfig;
}

export interface OrbSubscription {
  orbSubscriptionId: string;
  planName: string;
  planId: string;
  createdAt: string;
  startDate: string;
  endDate: string | null;
  status: 'active' | 'ended' | 'upcoming';
}

export type BillingSettingsInfo =
  | { kind: 'legacy-enterprise' }
  | {
      kind: 'orb-enterprise';
      orbEnterpriseInfo: OrbEnterpriseInfo;
      orbSubscriptionHistory: OrbSubscription[];
    }
  | {
      kind: 'orb-business';
      orbInfo: OrbInfo;
      orbSubscriptionHistory: OrbSubscription[];
    }
  | { kind: 'orb-pay-as-you-go'; orbInfo: OrbInfo };

// --- Referrals ---
export interface ReferredPayment {
  referredUser: { name: string; email: string };
  referredOrg: { name: string };
  referredPaymentReceivedAt: Date | string;
  referredPaymentAmount: number;
  payoutFormulaId: string;
  commissionAmount: number;
  commissionDisbursedAt: Date | string | null;
}

// --- ToolsV2 ---
/** Where a tool can be invoked from. */
export type ToolContextKind = 'standalone' | 'conversation' | 'workflow';

export interface ToolListItem {
  key: string;
  implementationKey: string;
  name: string;
  description: string;
  defaultDescription?: string;
  provider: string;
  providerDisplayName: string;
  providerDescription?: string;
  implementationType: 'builtin' | 'agent' | 'workflow' | 'custom_js' | 'legacy';
  toolType?: 'query' | 'mutation' | 'interruptable';
  /**
   * Every context this tool can run in. Filter on it before offering a tool —
   * a chat-only tool dropped into a workflow ToolCall node throws at run time.
   * Optional so a server that predates the field is treated as "no constraint".
   */
  availableContexts?: Array<ToolContextKind>;
  credentialStrategyDescriptors: Array<{
    name: string;
    fieldNames?: string[];
    displayLabel?: string;
  }>;
  credentialCacheTTL?: number;
  inputTy: Ty.ExtendedAttributeType;
  /** Per-field descriptions for top-level input params (dropped from `inputTy`). */
  inputFieldDescriptions?: Record<string, string>;
  outputTy: Ty.ExtendedAttributeType;
  requiredResources: Record<string, number>;
  canExecute: boolean;
  notExecutableReason?: string;
  hidden?: boolean;
  workflowStepCount?: number;
}

export type ToolsV2Strategy =
  | { name: string; t: 'custom' }
  | { name: string; t: 'workos'; provider: string };

export interface CredentialStrategyInfo {
  displayName: string;
  description: string;
  credentialFields: Array<{
    name: string;
    label: string;
    type: 'text' | 'password' | 'textarea';
    required: boolean;
    placeholder?: string;
  }>;
  strategy: ToolsV2Strategy;
}

export interface ToolCredentialInfoResponse {
  toolKey: string;
  provider: string;
  displayName: string;
  strategies: CredentialStrategyInfo[];
}

export interface ProviderAccountListItem {
  id: string;
  displayName: string;
  status: 'active' | 'default' | 'disabled' | 'archived';
  isBuiltIn: boolean;
  createdAt: string;
}

export interface ProviderWithAccounts {
  key: string;
  displayName: string;
  accounts: ProviderAccountListItem[];
}

// --- LLM ---
export interface AsyncCompletionResponse {
  status: 'pending' | 'success';
  completion: unknown | null;
}

export interface LlmColConfigV2 {
  t: 'llm';
  version: 'v2';
  outputTy: string;
  editorState: unknown;
  model: string;
  schedule?: ColWhen;
  temperature?: number;
  maxOutputTokens?: number;
  topP?: number;
  topK?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
}

export interface LlmMessage {
  role: 'user' | 'assistant' | 'system' | string;
  content: unknown;
  [key: string]: unknown;
}

// --- Resource ---
export type ResourceType =
  | 'chat'
  | 'agent'
  | 'folder'
  | 'document'
  | 'workflow'
  | 'dataset'
  | 'workflow_trigger';

// --- Resource Chat sub-routes ---
export interface ChatStatusEvent {
  id: string;
  eventType: string;
  createdAt: string;
  metadataId: string | null;
}

export interface ChatStatusFrame {
  id: string;
  parentId: string | null;
  createdAt: string;
  fpType: string;
  displayName: string;
  depth: number;
  position: number;
  status: ATCStackFrameStatus;
  credits: number;
  events: ChatStatusEvent[];
  children: ChatStatusFrame[];
  lastError?: string;
}

export interface ChatCostTotals {
  credits: number;
  hasActiveFrame: boolean;
  breakdown: { llm: number; toolcall: number };
}

export interface ChatStatusResponse {
  stackFrames: ChatStatusFrame[];
  totals: ChatCostTotals;
}

export interface StoredMessage {
  id: string;
  role: string;
  position: number;
  createdAt: Date | string;
  updatedAt: Date | string;
  usage: unknown | null;
  message: unknown;
  /** Message kind; 'hidden' entries are internal and not shown to users. */
  type?: string;
  messageId?: string | null;
}

export interface ConversationInfo {
  id: string;
  orgId: string;
  actorId: string;
  agent: string | null;
  agentId?: string | null;
  title: string;
  status: 'active' | 'archived';
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface ListMessagesResponse {
  conversation: ConversationInfo;
  messages: StoredMessage[];
}

export interface ListParticipantsResponse {
  participants: Array<{ id: string; name: string | null }>;
}

export interface ConversationSnapshotChat {
  id: string;
  org_id: string;
  owner_org_id?: string;
  actor_id: string;
  agent: string | null;
  agent_id: string | null;
  title: string;
  status: 'active' | 'archived';
  metadata: { loadedToolIds?: string[] } | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationSnapshotMessageRow {
  id: string;
  org_id: string;
  actor_id: string;
  chat_id: string;
  message_id: string | null;
  position: number;
  role: string;
  message: unknown;
  usage: unknown | null;
  type: string;
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationSnapshotActionRow {
  id: string;
  org_id: string;
  chat_id: string;
  type: string;
  status: string;
  data: unknown | null;
  completed_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationSnapshot {
  chat: ConversationSnapshotChat;
  messages: ConversationSnapshotMessageRow[];
  actions: ConversationSnapshotActionRow[];
  children?: ConversationSnapshot[];
}

export interface GetConversationSnapshotResponse {
  conversation: ConversationSnapshot;
}

export interface ChatAction {
  id: string;
  orgId: string;
  chatId: string;
  type: string;
  status: string;
  data: unknown | null;
  completedAt: Date | string | null;
  approvedAt: Date | string | null;
  approvedBy: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface SubAgentSnapshotResponse {
  stackFrames: ChatStatusFrame[];
  messages: StoredMessage[];
  actions: ChatAction[];
}

export type ChatAddAction =
  | { type: 'focus_agent'; agentId: string }
  | { type: 'unfocus_agent' }
  | {
      type: 'add_artifact';
      artifactType: string;
      artifactId?: string;
      data?: unknown;
    }
  | {
      type: 'add_tool_result_artifact';
      messageId: string;
      artifactType: string;
      artifactId: string;
      data?: unknown;
      toolKey?: string;
    }
  | { type: 'remove_artifact'; artifactId: string }
  | { type: 'remove_tool'; toolKey: string }
  | { type: 'auto_approve' }
  | { type: 'remove_auto_approve' }
  | { type: 'enter_plan_mode' }
  | { type: 'exit_plan_mode' };

/** Chat POST body (v20260601) plus route `conversationId` kept on context for the client. */
export type ChatParams = Omit<ChatPostBodyV20260601, 'context'> & {
  context: ChatPostBodyV20260601['context'] & {
    conversationId: string;
    title?: string;
    mode?: 'auto' | 'manual';
  };
};

// --- Resource Agent sub-routes ---
export interface AgentEditorState {
  type: 'doc';
  content?: unknown[];
}

export interface AgentOutput {
  id: string;
  orgId: string;
  ownerId: string;
  agentKey: string;
  editorState: unknown;
  model: string;
  locked: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
  title: string | null;
  description: string | null;
}

export interface AgentMentionedToolItem {
  key: string;
  name: string;
  canExecute: boolean;
  notExecutableReason?: string;
}

export interface GetMentionedToolsStatusResponse {
  toolsRunInOwnerOrg: boolean;
  tools: AgentMentionedToolItem[];
  /** The agent's current input type, as an `Ty.displayTy` string. */
  inputShape: string;
  /**
   * The agent's current output type (from its `response-shape` node), as an
   * `Ty.displayTy` string. Plain `string`/`super` when no response shape is set.
   */
  outputShape: string;
}

// --- Resource Document sub-routes ---
export interface DocumentOutput {
  id: string;
  orgId: string;
  ownerId: string;
  editorState: unknown;
  locked: boolean;
  title: string | null;
  description: string | null;
  status: 'active' | 'archived';
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface DatasetArtifactMetadataOutput {
  rowCount: number | null;
  /**
   * Rows the artifact was taken from, when the writer recorded it. Greater than
   * `rowCount` means the artifact is a sample — the one thing a reader holding
   * only the file cannot work out for itself.
   */
  sourceRowCount?: number | null;
  estimatedSizeBytes: number | null;
  schema: {
    columns: Array<{
      name: string;
      type: string | null;
    }>;
  };
}

/**
 * How big a dataset is, against how much of it the artifact the preview
 * downloads actually carries.
 *
 * `totalRows` greater than `artifactRows` means the grid is looking at a
 * sample, which is the one thing it cannot work out for itself — nothing in the
 * artifact says what it was taken from. Either is null when the dataset has no
 * artifact yet, or when its kind cannot answer without a scan it will not pay
 * for; an absent disclosure is recoverable, a wrong one is not.
 */
export interface DatasetRowCountOutput {
  artifactRows: number | null;
  totalRows: number | null;
}

export interface DatasetArtifactOutput {
  id: string;
  datasetId: string;
  pointer: string;
  hash: string;
  metadata?: DatasetArtifactMetadataOutput | null;
  expiresAt: Date | string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  lastAccessedAt: Date | string | null;
}

/**
 * Where an artifact's parquet should be read from.
 *
 * `signed` is a bucket URL the browser can range-read directly, which is what
 * makes a preview open on the footer rather than on a full download. `proxied`
 * is the development answer — nothing to sign against — and means falling back
 * to the API's own streaming route.
 */
export type DatasetArtifactUrl =
  | { kind: 'signed'; url: string; expiresAt: string }
  | { kind: 'proxied' };

export type DatasetColumnDisplayOptionsOutput = {
  numberFormat: 'number' | 'percent' | 'compact';
  decimals: number | null;
  alignment: 'left' | 'center' | 'right';
  inCellBar: boolean;
  barMax?: number;
};

export interface DatasetColumnSettingOutput {
  columnName: string;
  displayOptions: DatasetColumnDisplayOptionsOutput;
}

export interface DatasetOutput {
  id: string;
  orgId: string;
  ownerId: string;
  type: 'csv' | 'manual' | 'warehouse' | 'snowflake' | 'postgres' | 'bigquery';
  name: string;
  originalFilename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  /** Declared business key. Null falls back to an internal, import-unstable id. */
  rowKeyColumns?: string[] | null;
  /**
   * Where the dataset's rows are in getting here. A CSV import finishes after
   * the request that started it, so `importing` means the rows are still being
   * read — distinct from a dataset that genuinely has none.
   */
  importStatus?: 'importing' | 'ready' | 'failed';
  importError?: string | null;
  /** The SELECT a warehouse dataset is seeded from; null for other kinds. */
  sourceQuery?: string | null;
  /**
   * True while a source change-detection pass (a scheduled scan or a manual
   * detect) is re-pulling the preview, for the "checking for changes" indicator.
   */
  checkingForChanges?: boolean;
  /**
   * Cron driving those scheduled passes. Null is push-only: evaluated when a
   * write announces a change, and for a warehouse dataset — which cannot
   * announce one — never. Served so the schedule can be shown and changed after
   * the dataset is created, not only chosen at it.
   */
  cadence?: string | null;
  /** IANA timezone the cadence cron is read in. Defaults to UTC. */
  cadenceTimezone?: string;
  /**
   * The warehouse table workflow write-backs land in, when one was named. Null
   * falls back to the table a plain single-table source query reads.
   */
  writeBackTable?: { schema: string; name: string } | null;
  /**
   * Preview capabilities for this dataset kind. Kinds that cannot be read
   * through an ad-hoc SELECT carry no query at all.
   */
  preview: { queryable: false } | { queryable: true; query: string | null };
  status: 'active' | 'archived';
  createdAt: Date | string;
  updatedAt: Date | string;
  /**
   * Ids of attached workflows the grid expands into a column showing each one's
   * most-recent run output for a row. Null or omitted means expand nothing.
   */
  expandedAutomationWorkflowIds?: string[] | null;
  artifacts?: DatasetArtifactOutput[];
  /**
   * Saved per-column display options, shared across everyone who opens the
   * dataset. Only columns a user has customized appear here.
   */
  columnSettings?: DatasetColumnSettingOutput[];
  /**
   * True while the dataset is scoped to the chat that created it (from a file
   * upload) and hidden from the library. Promoting it flips this to false.
   */
  chatScoped?: boolean;
  /**
   * The chat this dataset was uploaded into, if any. Kept for provenance even
   * after promotion.
   */
  sourceChatId?: string | null;
}

// --- Resource Workflow sub-routes ---
export interface WorkflowDefinition {
  id: string;
  orgId: string;
  ownerId: string;
  name: string;
  status: 'active' | 'archived';
  workflowStatus?: string;
  workflowType?: string;
  idempotencyKey?:
    | {
        strategy: 'value';
        pointer: string;
      }
    | {
        strategy: 'value';
        template: string;
      }
    | null;
  graph?: unknown;
  createdAt: Date | string;
  updatedAt: Date | string;
  schedule?: string;
  timezone?: string;
  cronTriggerId?: string;
  agentId?: string | null;
  agentTitle?: string | null;
  prompt?: string;
}

export interface ScheduledAgentWorkflowOutput extends WorkflowDefinition {
  cronTriggerId: string;
}

export interface ScheduledAgentBundleCronTrigger {
  kind: 'cron';
  id: string;
  schedule: string;
  timezone: string;
  enabled: boolean;
}

export interface ScheduledAgentBundleWebhookTrigger {
  kind: 'webhook';
  id: string;
  enabled: boolean;
}

export type ScheduledAgentBundleReadTrigger =
  | ScheduledAgentBundleCronTrigger
  | ScheduledAgentBundleWebhookTrigger;

export interface CreateScheduledAgentBundleResponse {
  workflow: ScheduledAgentWorkflowOutput;
  trigger: ScheduledAgentBundleCronTrigger;
}

export interface GetScheduledAgentBundleResponse {
  workflow: WorkflowDefinition;
  trigger: ScheduledAgentBundleReadTrigger;
}

export type WorkflowRunStats = {
  bucket: 'hour' | 'day';
  /** Run counts per (time bucket, status), matching the request filters. */
  points: { bucketStart: string; status: string; count: number }[];
};

export interface WorkflowRunSummary {
  id: string;
  workflowId: string;
  /** The attempt this run replays; null for a first attempt. */
  retryOf?: string | null;
  /** 0-based attempt index within the retry chain (original = 0). */
  retryNum?: number;
  triggerType: string;
  triggerId: string;
  status: string;
  error: string | null;
  startedAt: Date | string | null;
  finishedAt: Date | string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  /**
   * The most items any single step processed (1 for a linear run, N for a
   * for-each over N). Absent on older servers; treat missing as unknown.
   */
  itemsProcessed?: number | null;
  /**
   * The resolved idempotency key the run was dispatched under; null when the
   * workflow has none configured or it did not resolve. Absent on older servers.
   */
  idempotencyKey?: string | null;
}

export interface WorkflowRunOutput {
  nodeId: string;
  name: string;
  output: unknown;
}

/** Credits spent by a workflow run, split by usage kind. */
export interface WorkflowRunCost {
  credits: number;
  breakdown: { llm: number; toolcall: number };
}

export interface WorkflowRunDetail extends WorkflowRunSummary {
  /** A newer attempt that supersedes this one; null when this is the latest. */
  retriedByRunId?: string | null;
  /** Hydrated trigger input payload from kv_store (null if none). */
  input: unknown | null;
  /** Hydrated final outputs, one per terminal (leaf) node that produced one. */
  outputs: WorkflowRunOutput[];
  /** Total credits spent across the run's agent/tool node executions. */
  cost: WorkflowRunCost;
}

export interface WorkflowRunStep {
  id: string;
  nodeId: string;
  stepIndex: number;
  status: string;
  error: string | null;
  startedAt: Date | string | null;
  finishedAt: Date | string | null;
  /** When the step row was created — a reliable proxy for when it started. */
  createdAt: Date | string;
  /** The run-definition node type (e.g. 'agent', 'system/run-input'). */
  nodeType: string | null;
  /** Display label for the step (node name, or a humanized type). */
  name: string;
  /** For agent nodes, the id of the agent that ran (else null). */
  agentId: string | null;
  /** The exact linked ATC run id for this step's root instance (else null). */
  atcRunId: string | null;
  /** The agent run's conversation id, parsed from its run key (else null). */
  chatId: string | null;
}

/** A node in a run's materialized definition graph, with its run state + I/O ids. */
export interface WorkflowRunGraphNode {
  /** The run-scoped node id (`workflow_run_definition_nodes.run_node_id`). */
  nodeId: string;
  /** The node type (e.g. 'agent', 'tool-call', 'system/run-input'). */
  type: string;
  /** The node's persisted `data` payload (config). */
  data: unknown;
  /** Display label (configured `data.name`, else humanized type). */
  name: string;
  inputShape: string | null;
  defaultOutput: unknown;
  /** The step status for this node (null if no step row yet). */
  status: string | null;
  error: string | null;
  /** Root-instance input kv id (null if none). */
  inputId: string | null;
  /** Root-instance output kv id (null if none). */
  outputId: string | null;
  /** Agent run conversation id, parsed from the linked ATC run key (else null). */
  chatId: string | null;
  /**
   * Light step-instance summaries for graph fan-out (no hydrated I/O).
   * Empty when the step has not been created yet.
   */
  instances: WorkflowRunGraphInstanceSummary[];
}

/** Instance summary on the run graph (status only; I/O via getRunStepPayload). */
export interface WorkflowRunGraphInstanceSummary {
  instanceKey: string;
  parentInstanceKey: string | null;
  status: string;
  error: string | null;
}

export interface WorkflowRunStepInstancePayload {
  instanceKey: string;
  parentInstanceKey: string | null;
  status: string;
  error: string | null;
  inputId: string | null;
  outputId: string | null;
  input: unknown | null;
  output: unknown | null;
  /** Agent conversation id from the linked ATC run key, if any. */
  chatId: string | null;
  startedAt: Date | string | null;
  finishedAt: Date | string | null;
}

export interface WorkflowRunStepPayload {
  nodeId: string;
  inputId: string | null;
  outputId: string | null;
  /**
   * Convenience I/O: root instance when present, else the sole instance.
   * Prefer `instances` for multi-instance (for-each fan-out) steps.
   */
  input: unknown | null;
  output: unknown | null;
  /** All step instances for this node, oldest first. */
  instances: WorkflowRunStepInstancePayload[];
}

export interface WorkflowRunGraphEdge {
  id: string;
  fromNodeId: string;
  toNodeId: string;
}

export interface WorkflowRunGraph {
  nodes: WorkflowRunGraphNode[];
  edges: WorkflowRunGraphEdge[];
}

export interface WorkflowTriggerView {
  bindingId: string;
  triggerType: string;
  triggerId: string;
  name: string;
  bindingEnabled: boolean;
  priority: number;
  defaultInput: unknown;
  createdAt: Date | string;
  updatedAt: Date | string;
  cron: { schedule: string; timezone: string; enabled: boolean } | null;
  webhook: { enabled: boolean } | null;
}

// Aliased to the shared provider-key enum owned by the API contract so this
// hand-typed client can't drift out of sync with the real provider list.
export type WorkflowWebhookProviderKey = WebhookProviderKey;

export type WebhookProviderCatalogEvent = {
  key: string;
  displayName: string;
  description: string;
  externalEventTypes: readonly string[];
  /** Channel/category grouping for the event picker, when the provider sets it. */
  group?: string;
  requiresConfig: boolean;
  supportsSimulation: boolean;
  configSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  outputShape?: string;
  runOutputShape?: string;
  samplePayload?: unknown;
};

export type WebhookProviderCredentialField = {
  name: string;
  label: string;
  type: 'text' | 'password' | 'textarea';
  required: boolean;
  placeholder?: string;
};

export type WebhookProviderCredentials = {
  kind: 'provider_account';
  providerKey: string;
  fields: readonly WebhookProviderCredentialField[];
  docsUrl?: string;
};

export type WebhookProviderCatalogEntry = {
  key: WorkflowWebhookProviderKey;
  displayName: string;
  description: string;
  status: 'active' | 'beta' | 'deprecated';
  endpoint: {
    scope: 'per_trigger' | 'per_connection' | 'per_org_provider';
    secretKind: 'cotera_generated' | 'provider_supplied' | 'both';
    singleWebhookEndpoint?: boolean;
  };
  setup: {
    kind:
      | 'api_event_subscription'
      | 'set_webhook_url'
      | 'single_stream_multiplex'
      | 'manual_instructions'
      | 'app_level_then_user_oauth'
      | 'none';
    supportsReinstall: boolean;
    requiresTeardown: boolean;
  };
  events: readonly WebhookProviderCatalogEvent[];
  verificationConfigSchema?: Record<string, unknown>;
  credentials?: WebhookProviderCredentials;
  /**
   * For providers pinned to one delivery URL, whether the org already supplied
   * the signing secret every trigger on that endpoint verifies against. Absent
   * for providers that keep a secret per trigger.
   */
  sharedEndpointSecretConfigured?: boolean;
};

export type WebhookProviderCatalogOutput = {
  providers: readonly WebhookProviderCatalogEntry[];
};

export type WebhookTriggerSettings = {
  id: string;
  name: string;
  enabled: boolean;
  providerKey: WorkflowWebhookProviderKey;
  verificationConfig: unknown;
  eventKey: string;
  eventConfig: unknown;
  installStatus: 'draft' | 'installing' | 'active' | 'error' | 'archived';
  installError: { code?: string; message?: string } | null;
  deliveryUrl: string;
  secret?: string;
};

export interface WorkflowNode {
  nodeId: string;
  edgeId?: string;
}

// --- Resource Folder sub-routes ---
export interface FolderTreeLeafItem {
  displayName: string;
  pointer: string;
  type: ResourceType;
  position: number;
}

export interface FolderTreeFolder {
  id: string;
  ownerId: string;
  parentId: string | null;
  name: string;
  position: number;
  createdAt: Date | string;
  updatedAt: Date | string;
  items: FolderTreeLeafItem[];
  visibility: 'private' | 'shared';
  sharedBucket?: 'org' | 'external';
}

export type FolderItemsResponse = {
  items: Array<
    | { type: 'folder'; data: ResourceListFolder }
    | { type: 'chat'; data: ResourceListChat }
    | { type: 'agent'; data: ResourceListAgent }
    | { type: 'document'; data: ResourceListDocument }
    | { type: 'workflow'; data: ResourceListWorkflow }
    | { type: 'dataset'; data: ResourceListDataset }
    | { type: 'workflow_trigger'; data: ResourceListWorkflowTrigger }
  >;
  total: number;
};

export type ResourceFolderItemsOutput = FolderItemsResponse;

/**
 * The calling user's favorites, in their chosen order.
 *
 * Deliberately the same item union a folder's contents arrive in: a favorite is
 * a pointer to an ordinary resource, so every display helper the Library
 * already has (`resourceViewOfItem`, the type icon, the row) reads one of these
 * unchanged. Favorites whose resource the user can no longer see are omitted by
 * the server rather than arriving as holes.
 */
export type ResourceFavoritesResponse = {
  items: FolderItemsResponse['items'];
};

export type FolderUnfiledTotals = Array<{ t: ResourceType; count: number }>;

export interface ResourceMeta {
  folderId: string | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  permission: 'view' | 'edit' | 'admin' | 'pending';
  hasUnreadAssistant?: boolean;
  users?: Array<{ t: 'user'; id: string } | { t: 'org'; orgId?: string }>;
}

export type ResourceGetResponse =
  | {
      type: 'agent';
      agent: {
        id: string;
        orgId: string;
        ownerId: string;
        agentKey: string;
        editorState: unknown;
        model: string;
        locked: boolean;
        createdAt: Date;
        updatedAt: Date;
        title: string | null;
        description: string | null;
        type: string | null;
        status: 'active' | 'archived';
        emoji?: string | null;
        sourceConversationId?: string | null;
      };
      meta?: ResourceMeta;
    }
  | {
      type: 'document';
      document: {
        id: string;
        orgId: string;
        ownerId: string;
        editorState: unknown;
        locked: boolean;
        title: string | null;
        description: string | null;
        status: 'active' | 'archived';
        createdAt: Date;
        updatedAt: Date;
        sourceConversationId?: string | null;
      };
      meta?: ResourceMeta;
    }
  | {
      type: 'chat';
      chat: {
        id: string;
        orgId: string;
        actorId: string;
        agent: string | null;
        agentId?: string | null;
        title: string;
        status: 'active' | 'archived';
        createdAt: Date;
        updatedAt: Date;
        hasUnreadAssistant?: boolean;
      };
      meta?: ResourceMeta;
    }
  | {
      type: 'folder';
      folder: {
        id: string;
        ownerId: string;
        parentId: string | null;
        name: string;
        position: number;
        createdAt: Date;
        updatedAt: Date;
        scope: 'user' | 'org' | 'external';
        scopeData?: Record<string, unknown>;
        childrenCount?: number;
        chatCount?: number;
      };
    }
  | {
      type: 'workflow';
      workflow: {
        id: string;
        orgId: string;
        ownerId: string;
        name: string;
        status: 'active' | 'archived';
        workflowStatus?: string;
        workflowType?: string;
        idempotencyKey?:
          | {
              strategy: 'value';
              pointer: string;
            }
          | {
              strategy: 'value';
              template: string;
            }
          | null;
        graph?: unknown;
        createdAt: Date;
        updatedAt: Date;
        schedule?: string;
        timezone?: string;
        cronTriggerId?: string;
        agentId?: string | null;
        agentTitle?: string | null;
        prompt?: string;
      };
      meta?: ResourceMeta;
    }
  | {
      type: 'dataset';
      dataset: DatasetOutput;
      meta?: ResourceMeta;
    }
  | {
      type: 'workflow_trigger';
      workflowTrigger: {
        id: string;
        orgId: string;
        ownerId: string;
        name: string;
        enabled: boolean;
        triggerKind: 'cron' | 'webhook' | 'event';
        status: 'active' | 'archived';
        createdAt: Date;
        updatedAt: Date;
        cron: { schedule: string; timezone: string; enabled: boolean } | null;
        webhook: { enabled: boolean } | null;
        event?: { eventName: string; enabled: boolean } | null;
      };
      meta?: ResourceMeta;
    };

export interface ResourceGetParams {
  type: ResourceType;
  id: string;
}

export interface ResourceListParams {
  type: ResourceType;
  limit?: number;
  offset?: number;
  folderScope?: 'private' | 'shared';
  /** Ownership lens: `mine` = owned by actor; `shared` = inbound user grants. */
  ownership?: 'mine' | 'shared';
  search?: string;
  sort?: string;
  sortDir?: 'asc' | 'desc';
  workflowScheduledAgentOnly?: boolean;
  unreadOnly?: boolean;
  /**
   * When listing chats, only return chats with no folder placement visible to the
   * viewer (sidebar Unfiled sections). Combine with `folderScope` to bucket by
   * private vs shared.
   */
  unfiledOnly?: boolean;
  agentId?: string;
  triggerSources?: ChatTriggerSource[];
  /**
   * Defaults to true. When listing chats, pass `false` to skip the exact count
   * over every matching chat; `total` then reports the size of this page. The
   * count is a full pass over the matched set, so opt out whenever the caller
   * renders a page and never shows a total.
   */
  includeTotal?: boolean;
  /**
   * Filter by trigger kind. On workflow triggers, `cron`/`webhook`/`event`/
   * `dataset` select that kind. On workflows, `none` returns only workflows
   * with no trigger bound.
   */
  triggerKind?: 'cron' | 'webhook' | 'event' | 'dataset' | 'none';
}

/** A principal (owner + explicit grants) with access to a resource. Org-wide grants carry no id. */
export type ResourceAccessEntry =
  | { t: 'user'; id: string }
  | { t: 'org'; orgId?: string };

export interface ResourceListAgent {
  id: string;
  orgId: string;
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName?: string | null;
  ownerId: string;
  agentKey: string;
  model: string;
  description: string | null;
  type: string | null;
  status: 'active' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  title?: string | null;
  folderId?: string | null;
  folderName?: string | null;
  folderPath?: string[] | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  emoji?: string | null;
  /** Owner + explicit grantees, for "shared with N" + who-has-access hover. */
  users?: ResourceAccessEntry[];
  /** The conversation this agent was created in, or null if not chat-created. */
  sourceConversationId?: string | null;
  /** Title of the source conversation, for the Library's group-by-origin view. */
  sourceConversationTitle?: string | null;
}

export interface ResourceListDocument {
  id: string;
  orgId: string;
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName?: string | null;
  ownerId: string;
  locked?: boolean;
  title: string | null;
  description: string | null;
  status: 'active' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  folderId?: string | null;
  folderName?: string | null;
  folderPath?: string[] | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  /** Effective visibility scope: folder scope combined with direct shares on the document. */
  scope?: 'user' | 'org' | 'external' | null;
  users?: ResourceAccessEntry[];
  /** The conversation this document was created in, or null if not chat-created. */
  sourceConversationId?: string | null;
  /** Title of the source conversation, for the Library's group-by-origin view. */
  sourceConversationTitle?: string | null;
}

/** One resource an Organize proposal would file into a folder. */
export interface OrganizeProposalMember {
  type: ApiResourceType;
  id: string;
  title: string;
  currentFolderId: string | null;
  currentFolderName: string | null;
}

/** One topic's resources and the folder they'd be organized into. */
export interface OrganizeProposal {
  /** Synthetic id for this suggestion; the review UI keys rows off it. */
  groupId: string;
  /** Folder name to create, from the model. */
  suggestedFolderName: string;
  /** One line on why these belong together. */
  reason: string;
  /** When set, consolidate into this existing folder instead of creating one. */
  existingFolderId: string | null;
  existingFolderName: string | null;
  members: OrganizeProposalMember[];
}

/** One folder in a staged organize draft (shared between the panel and Coco). */
export interface OrganizeDraftFolder {
  draftId: string;
  name: string;
  existingFolderId: string | null;
  existingFolderName: string | null;
  /** True for a folder the user added — kept even when empty. */
  userAdded?: boolean;
  /**
   * draftId of this folder's parent in the staged plan, or null/absent for a
   * top-level folder. Drives the nested display in the review panel.
   */
  parentDraftId?: string | null;
  members: OrganizeProposalMember[];
}

export interface OrganizeDraft {
  conversationId: string;
  orgId: string;
  userId: string;
  /** 'org' = organizing the org's shared library; 'user'/absent = personal. */
  scope?: 'user' | 'org';
  folders: OrganizeDraftFolder[];
  updatedAt: number;
}

export interface OrganizeDraftResponse {
  draft: OrganizeDraft | null;
}

export interface OrganizeOpenResponse {
  /** The conversation to navigate to (reused if one was already open). */
  conversationId: string;
}

export interface OrganizeCommitResponse {
  folders: number;
  items: number;
}

export interface ResourceListFolder {
  id: string;
  /** Owner org of this folder (from `folders.owner_org_id`); aligns folders with the other list-item types. */
  orgId?: string;
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName?: string | null;
  ownerId: string;
  parentId: string | null;
  name: string;
  position: number;
  createdAt: Date;
  updatedAt: Date;
  scope: 'user' | 'org' | 'external';
  scopeData?: Record<string, unknown>;
  childrenCount?: number;
  chatCount?: number;
  users?: ResourceAccessEntry[];
}

export interface ResourceListChat {
  id: string;
  orgId: string;
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName?: string | null;
  actorId: string;
  agent: string | null;
  agentId?: string | null;
  title: string;
  status: 'active' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  hasUnreadAssistant?: boolean;
  lastMessage?: {
    id: string;
    role: string;
    position: number;
    createdAt: Date;
    updatedAt: Date;
    usage?: unknown;
    message?: unknown;
  } | null;
  firstMessage?: {
    id: string;
    role: string;
    position: number;
    createdAt: Date;
    updatedAt: Date;
    usage?: unknown;
    message?: unknown;
  } | null;
  folderId?: string | null;
  folderName?: string | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  triggerSource?: ChatTriggerSource | null;
  users?: ResourceAccessEntry[];
}

export interface ResourceListWorkflow {
  id: string;
  orgId: string;
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName?: string | null;
  ownerId: string;
  name: string;
  status: 'active' | 'archived';
  workflowStatus?: string;
  workflowType?: string;
  graph?: unknown;
  createdAt: Date;
  updatedAt: Date;
  folderId?: string | null;
  folderName?: string | null;
  folderPath?: string[] | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  schedule?: string;
  timezone?: string;
  cronTriggerId?: string;
  agentId?: string | null;
  agentTitle?: string | null;
  triggerName?: string | null;
  prompt?: string;
  users?: ResourceAccessEntry[];
}

export interface ResourceListDataset {
  id: string;
  orgId: string;
  ownerOrgName?: string | null;
  ownerId: string;
  /**
   * The same kinds `DatasetOutput` carries — the list serves warehouse datasets
   * too. Restated as `'csv' | 'manual'` this was simply wrong, and being wrong
   * in the narrowing direction meant nothing downstream could be type-checked
   * against a new kind.
   */
  type: DatasetOutput['type'];
  name: string;
  originalFilename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  status: 'active' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  folderId?: string | null;
  folderName?: string | null;
  folderPath?: string[] | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  scope?: 'user' | 'org' | 'external' | null;
  users?: ResourceAccessEntry[];
}

export interface ResourceListWorkflowTrigger {
  id: string;
  orgId: string;
  ownerId: string;
  name: string;
  enabled: boolean;
  triggerKind: 'cron' | 'webhook' | 'event' | 'dataset';
  status: 'active' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  folderId?: string | null;
  folderName?: string | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  cronSchedule?: string;
  cronTimezone?: string;
  eventName?: string;
  /** Watched dataset when `triggerKind` is `dataset`. */
  datasetId?: string;
  /** Display name of the watched dataset when `triggerKind` is `dataset`. */
  datasetName?: string | null;
  /** What the dataset trigger watches for when `triggerKind` is `dataset`. */
  datasetCriterion?: DatasetTriggerCriterion;
  /** Whether the dataset trigger fires on its own, or only when asked. */
  datasetRunMode?: DatasetTriggerRunMode;
  webhookProviderKey?: string | null;
  /** Address an `email` webhook listens on; null for every other provider. */
  inboundEmailAddress?: string | null;
  /** Provider event this webhook listens for; resolved against the catalog for display. */
  webhookEventKey?: string | null;
  workflowId?: string;
  agentId?: string | null;
  agentTitle?: string | null;
}

export type ResourceListOutput =
  | { type: 'agent'; items: ResourceListAgent[]; total: number }
  | { type: 'document'; items: ResourceListDocument[]; total: number }
  | { type: 'folder'; items: ResourceListFolder[]; total: number }
  | { type: 'chat'; items: ResourceListChat[]; total: number }
  | { type: 'workflow'; items: ResourceListWorkflow[]; total: number }
  | { type: 'dataset'; items: ResourceListDataset[]; total: number }
  | {
      type: 'workflow_trigger';
      items: ResourceListWorkflowTrigger[];
      total: number;
    };

export interface ResourceShareTarget {
  type: ResourceType;
  id: string;
}

export type ResourceSharePostActivationStep =
  | { t: 'navigate'; path: string }
  | { t: 'chat'; prompt: string };

export interface ResourceSharePostActivationSequence {
  steps: ResourceSharePostActivationStep[];
}

export interface CreateResourceShareLinkParams {
  targets: ResourceShareTarget[];
  shareMode: 'access' | 'copy';
  grantTargetType?: 'user' | 'org';
  permission?: 'view' | 'edit';
  resourceFilter?: Record<string, unknown>;
  postActivationSequence?: ResourceSharePostActivationSequence;
  expiresInDays?: number;
  /** Who may open the link. The server defaults to `org`. */
  audience?: ResourceShareLinkAudience;
}

export type ResourceShareLinkAudience = 'org' | 'anyone';

/** Who can reach a resource and how — the share dialog's source of truth. */
export type ResourceAccess = ResourceAccessResponse;

export type ResourceSharePreview = ResourceShareLinkPreviewResponse;

/** A live link on a resource, for "Manage links". Dates arrive as ISO strings. */
export interface ResourceActiveShareLink {
  id: string;
  shareMode: 'access' | 'copy';
  permission: 'view' | 'edit' | null;
  audience: ResourceShareLinkAudience;
  /** Set when the link is an emailed invite that only this address opens. */
  invitedEmail: string | null;
  targetCount: number;
  expiresAt: string;
  createdAt: string;
  createdBy: { userId: string; name: string | null; email: string | null };
  openedByCount: number;
}

export interface ActivateShareLinkOutput {
  mode: 'access' | 'copy';
  targets: ResourceShareTarget[];
  targetResourceType: ResourceType;
  targetResourceId: string;
  postActivationSequence:
    | ResourceSharePostActivationSequence
    | null
    | undefined;
  grantCreated: {
    granteeType: 'user' | 'org';
    permission: 'view' | 'edit';
  } | null;
  copiedResource: ResourceShareTarget | null;
  copiedResources: ResourceShareTarget[] | null;
}

// --- DevServer ---
export interface ManifestEventStream {
  specifier: StreamSpecifier;
  sinks: Record<
    string,
    {
      config: { t: string };
      condition: unknown;
      select: Record<string, unknown>;
    }
  >;
  when: ColWhen;
  identifier: unknown;
  rel: unknown;
}

export interface ManifestMeta {
  version: string;
  materializations: Array<{
    schema: string;
    viewName: string;
    schedule: string | { cron: string };
  }>;
  definitions: Record<
    string,
    {
      id: string;
      displayName: string;
      hidden: boolean;
      attributes: Record<string, unknown>;
    }
  >;
  schemas: { read: string[] };
  eventStreams: ManifestEventStream[];
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TypedClientConfig {
  apiUrl: string;
  getAccessToken?: (options?: {
    forceRefresh?: boolean;
  }) => Promise<string | undefined>;
}

export type TypedClientRequestOptions = {
  apiSchemaVersion?: string;
};

const withApiSchema = (version: string): TypedClientRequestOptions => ({
  apiSchemaVersion: version,
});

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const statusToErrorType = (status: number): string => {
  switch (status) {
    case 401:
      return 'Unauthorized';
    case 403:
      return 'Forbidden';
    case 404:
      return 'NotFound';
    case 400:
      return 'InvalidInput';
    case 500:
      return 'InternalServerError';
    default:
      return 'Unknown';
  }
};

// Preserves the structured `errors: GraphBuildFailure[]` payload (used by
// BuildError) when present, dropping anything that doesn't match the shape.
const parseBuildFailures = (raw: unknown): ApiErrBody['errors'] => {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const parsed = raw.flatMap((item) => {
    if (!isRecord(item) || !isRecord(item['error'])) {
      return [];
    }
    const error = item['error'];
    if (typeof error['t'] !== 'string') {
      return [];
    }
    return [
      {
        error: {
          t: error['t'],
          message:
            typeof error['message'] === 'string' ? error['message'] : undefined,
        },
      },
    ];
  });
  return parsed.length > 0 ? parsed : undefined;
};

/** The machine-readable reason an endpoint gave, when it gave one. */
const readCode = (value: unknown): string | undefined =>
  isRecord(value) && typeof value['code'] === 'string'
    ? value['code']
    : undefined;

const parseApiErrBody = (status: number, data: unknown): ApiErrBody => {
  if (!isRecord(data)) {
    return { errorType: statusToErrorType(status) };
  }

  if (typeof data['errorType'] === 'string') {
    return {
      errorType: data['errorType'],
      message:
        typeof data['message'] === 'string' ? data['message'] : undefined,
      errors: parseBuildFailures(data['errors']),
      code: readCode(data),
    };
  }

  if (
    (data['t'] === 'server' || data['t'] === 'application') &&
    isRecord(data['err']) &&
    typeof data['err']['errorType'] === 'string'
  ) {
    return {
      errorType: data['err']['errorType'],
      message:
        typeof data['err']['message'] === 'string'
          ? data['err']['message']
          : undefined,
      errors: parseBuildFailures(data['err']['errors']),
      code: readCode(data['err']),
    };
  }

  const message = data['message'];
  if (isRecord(message)) {
    if (typeof message['errorType'] === 'string') {
      return {
        errorType: message['errorType'],
        message:
          typeof message['message'] === 'string'
            ? message['message']
            : undefined,
        errors: parseBuildFailures(message['errors']),
        code: readCode(message),
      };
    }
    if (
      (message['t'] === 'server' || message['t'] === 'application') &&
      isRecord(message['err']) &&
      typeof message['err']['errorType'] === 'string'
    ) {
      return {
        errorType: message['err']['errorType'],
        message:
          typeof message['err']['message'] === 'string'
            ? message['err']['message']
            : undefined,
        errors: parseBuildFailures(message['err']['errors']),
        code: readCode(message['err']),
      };
    }
  }

  const statusCode =
    typeof data['statusCode'] === 'number' ? data['statusCode'] : status;
  return {
    errorType: statusToErrorType(statusCode),
    message: typeof message === 'string' ? message : undefined,
  };
};

const toApiErr = (t: ApiErr['t'], err: Record<string, unknown>): ApiErr => ({
  t,
  err: {
    errorType:
      typeof err['errorType'] === 'string' ? err['errorType'] : 'Unknown',
    message: typeof err['message'] === 'string' ? err['message'] : undefined,
    errors: parseBuildFailures(err['errors']),
    code: readCode(err),
  },
});

export const parseApiErr = (status: number, data: unknown): ApiErr => {
  if (
    isRecord(data) &&
    (data['t'] === 'server' || data['t'] === 'application') &&
    isRecord(data['err'])
  ) {
    return toApiErr(data['t'], data['err']);
  }

  const message = isRecord(data) ? data['message'] : undefined;
  if (
    isRecord(message) &&
    (message['t'] === 'server' || message['t'] === 'application') &&
    isRecord(message['err'])
  ) {
    return toApiErr(message['t'], message['err']);
  }

  return { t: 'server', err: parseApiErrBody(status, data) };
};

const enc = (value: string | number): string =>
  encodeURIComponent(String(value));

/** Routes mounted at the API root (outside `/api/v1/`). */
const rootUrl = (config: TypedClientConfig, path: string): string =>
  `${config.apiUrl}/${path}`;

async function buildHeaders(
  config: TypedClientConfig,
  extra?: Record<string, string>,
  options?: TypedClientRequestOptions,
  tokenOptions?: { forceRefresh?: boolean }
): Promise<Record<string, string>> {
  const Authorization = await config.getAccessToken?.(tokenOptions);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra,
  };
  if (Authorization !== undefined) {
    headers['Authorization'] = Authorization;
  }
  if (options?.apiSchemaVersion !== undefined) {
    headers[API_SCHEMA_HEADER] = options.apiSchemaVersion;
  }
  return headers;
}

type HttpResponse<T> = {
  status: number;
  data: T;
};

const shouldRetryUnauthorized = (params: {
  status: number;
  isRetry: boolean;
  getAccessToken: TypedClientConfig['getAccessToken'];
}): boolean => {
  return (
    !params.isRetry &&
    params.status === 401 &&
    params.getAccessToken !== undefined
  );
};

async function requestWithUnauthorizedRetry<T>(params: {
  config: TypedClientConfig;
  options?: TypedClientRequestOptions;
  isRetry?: boolean;
  send: (headers: Record<string, string>) => Promise<HttpResponse<T>>;
}): Promise<HttpResponse<T>> {
  // On the retry after a 401, force a WorkOS session refresh: the rejected
  // token may still look valid client-side, so a plain getAccessToken() would
  // return the same token and 401 again.
  const headers = await buildHeaders(params.config, undefined, params.options, {
    forceRefresh: params.isRetry === true,
  });
  const response = await params.send(headers);
  if (
    shouldRetryUnauthorized({
      status: response.status,
      isRetry: params.isRetry ?? false,
      getAccessToken: params.config.getAccessToken,
    })
  ) {
    clearBearerTokenCache();
    return requestWithUnauthorizedRetry({
      ...params,
      isRetry: true,
    });
  }
  return response;
}

function toApiResult<T>(response: HttpResponse<T>): Result<T, ApiErr> {
  if (response.status >= 200 && response.status < 300) {
    return ok(response.data);
  }
  return err(parseApiErr(response.status, response.data));
}

async function get<T>(
  config: TypedClientConfig,
  path: string,
  params?: Record<string, unknown>,
  options?: TypedClientRequestOptions
): Promise<Result<T, ApiErr>> {
  const response = await requestWithUnauthorizedRetry<T>({
    config,
    options,
    send: (headers) =>
      axios.get<T>(`${config.apiUrl}/api/v1/${path}`, {
        params,
        headers,
        withCredentials: true,
        validateStatus: () => true,
      }),
  });
  return toApiResult(response);
}

async function post<T>(
  config: TypedClientConfig,
  path: string,
  data?: unknown,
  options?: TypedClientRequestOptions
): Promise<Result<T, ApiErr>> {
  const response = await requestWithUnauthorizedRetry<T>({
    config,
    options,
    send: (headers) =>
      axios.post<T>(`${config.apiUrl}/api/v1/${path}`, data, {
        headers,
        withCredentials: true,
        validateStatus: () => true,
      }),
  });
  return toApiResult(response);
}

const parseResponseText = <T>(text: string): T =>
  (text === ''
    ? null
    : (() => {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      })()) as T;

async function postRaw<T>(
  config: TypedClientConfig,
  path: string,
  body: XMLHttpRequestBodyInit,
  options?: TypedClientRequestOptions,
  // Reports upload progress as a fraction in [0, 1]. When provided, the request
  // is sent via XHR (fetch cannot surface `upload.onprogress`). Only covers the
  // upload of the body — the server-side parse afterwards is opaque.
  onUploadProgress?: (fraction: number) => void
): Promise<Result<T, ApiErr>> {
  const url = `${config.apiUrl}/api/v1/${path}`;
  const response = await requestWithUnauthorizedRetry<T>({
    config,
    options,
    send:
      onUploadProgress === undefined
        ? async (headers) => {
            const res = await fetch(url, {
              method: 'POST',
              headers: {
                ...headers,
                'Content-Type': 'application/octet-stream',
              },
              body,
              credentials: 'include',
            });
            return {
              status: res.status,
              data: parseResponseText<T>(await res.text()),
            };
          }
        : (headers) =>
            new Promise<HttpResponse<T>>((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open('POST', url);
              xhr.withCredentials = true;
              xhr.setRequestHeader('Content-Type', 'application/octet-stream');
              for (const [key, value] of Object.entries(headers)) {
                xhr.setRequestHeader(key, value);
              }
              xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                  onUploadProgress(event.loaded / event.total);
                }
              };
              xhr.onload = () =>
                resolve({
                  status: xhr.status,
                  data: parseResponseText<T>(xhr.responseText),
                });
              xhr.onerror = () =>
                reject(new Error('Network request failed during upload'));
              xhr.send(body);
            }),
  });
  return toApiResult(response);
}

async function postFormData<T>(
  config: TypedClientConfig,
  path: string,
  formData: FormData,
  options?: TypedClientRequestOptions
): Promise<Result<T, ApiErr>> {
  const response = await requestWithUnauthorizedRetry<T>({
    config,
    options,
    send: async ({ 'Content-Type': _dropped, ...headers }) => {
      const res = await fetch(`${config.apiUrl}/api/v1/${path}`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });
      const text = await res.text();
      const data =
        text === ''
          ? null
          : (() => {
              try {
                return JSON.parse(text);
              } catch {
                return text;
              }
            })();
      return {
        status: res.status,
        data: data as T,
      };
    },
  });
  return toApiResult(response);
}

/** GET for routes mounted at the API root (outside `/api/v1/`). */
async function rootGet<T>(
  config: TypedClientConfig,
  path: string,
  params?: Record<string, unknown>,
  options?: TypedClientRequestOptions
): Promise<Result<T, ApiErr>> {
  const response = await requestWithUnauthorizedRetry<T>({
    config,
    options,
    send: (headers) =>
      axios.get<T>(rootUrl(config, path), {
        params,
        headers,
        withCredentials: true,
        validateStatus: () => true,
      }),
  });
  return toApiResult(response);
}

/** POST for routes mounted at the API root (outside `/api/v1/`). */
async function rootPost<T>(
  config: TypedClientConfig,
  path: string,
  data?: unknown,
  options?: TypedClientRequestOptions
): Promise<Result<T, ApiErr>> {
  const response = await requestWithUnauthorizedRetry<T>({
    config,
    options,
    send: (headers) =>
      axios.post<T>(rootUrl(config, path), data, {
        headers,
        withCredentials: true,
        validateStatus: () => true,
      }),
  });
  return toApiResult(response);
}

/**
 * fetch-compatible wrapper for streaming endpoints (e.g. the playground chat
 * transport) that can't go through axios. Injects the bearer token and
 * retries once on 401 with a forced token refresh, mirroring
 * requestWithUnauthorizedRetry.
 */
function makeAuthenticatedFetch(config: TypedClientConfig): typeof fetch {
  const doFetch = async (
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    isRetry: boolean
  ): Promise<Response> => {
    const Authorization = await config.getAccessToken?.({
      forceRefresh: isRetry,
    });
    const headers = new Headers(init?.headers);
    if (Authorization !== undefined) {
      headers.set('Authorization', Authorization);
    }
    const response = await fetch(input, {
      ...init,
      headers,
      credentials: 'include',
    });
    if (
      shouldRetryUnauthorized({
        status: response.status,
        isRetry,
        getAccessToken: config.getAccessToken,
      })
    ) {
      clearBearerTokenCache();
      return doFetch(input, init, true);
    }
    return response;
  };
  return (input, init) => doFetch(input, init, false);
}

/**
 * Multipart POST for root routes. Drops the JSON Content-Type so the browser
 * sets the multipart boundary itself.
 */
async function rootPostFormData<T>(
  config: TypedClientConfig,
  path: string,
  formData: FormData
): Promise<Result<T, ApiErr>> {
  const response = await requestWithUnauthorizedRetry<T>({
    config,
    send: ({ 'Content-Type': _dropped, ...headers }) =>
      axios.post<T>(rootUrl(config, path), formData, {
        headers,
        withCredentials: true,
        validateStatus: () => true,
      }),
  });
  return toApiResult(response);
}

// ---------------------------------------------------------------------------
// CoteraApiV2 type
// ---------------------------------------------------------------------------

export type CoteraApiV2 = {
  // Tenanted namespace (.t.)
  t: {
    commits: {
      list: (
        _p?: Record<string, never>
      ) => Promise<Result<SavedCommit[], ApiErr>>;
      commit: (p: {
        commits: Commit[];
      }) => Promise<Result<{ commits: SavedCommit[] }, ApiErr>>;
    };
    artifacts: {
      create: (p: {
        ast: unknown;
        acceptStaleWithinSeconds?: number;
      }) => Promise<Result<Artifact, ApiErr>>;
      createByDefinitionId: (p: {
        defId: string;
      }) => Promise<Result<Artifact, ApiErr>>;
      getRequestById: (p: {
        id: string;
      }) => Promise<Result<ArtifactRequest | null, ApiErr>>;
      listRequests: (p: {
        sqlHash?: string;
        pagination?: { page: number; perPage?: number };
      }) => Promise<Result<ArtifactRequest[], ApiErr>>;
      usableCache: (
        _p?: Record<string, never>
      ) => Promise<Result<Record<string, UsableCacheEntry>, ApiErr>>;
      invalidateArtifact: (p: {
        id: string;
      }) => Promise<Result<Record<string, never>, ApiErr>>;
    };
    tools: {
      list: (_p?: Record<string, never>) => Promise<Result<ToolItem[], ApiErr>>;
      listConnections: (
        _p?: Record<string, never>
      ) => Promise<Result<ToolConnection[], ApiErr>>;
      updateToolsConnection: (p: {
        mode: 'single' | 'all';
        toolId?: string;
        connectionId: string;
        connectionData: {
          type: 'apiKey' | 'oauth' | 'password' | 'noAuth';
          provider: string;
          displayName: string;
          secret: ToolSecret;
        };
      }) => Promise<
        Result<
          { updatedTools: ToolItem[]; count: number; newConnectionId: string },
          ApiErr
        >
      >;
      execute: (p: {
        toolName: string;
        params: unknown;
        conversationId?: string;
        toolCallId: string;
      }) => Promise<Result<{ type: string; data: unknown }, ApiErr>>;
    };
    toolsV2: {
      list: (
        _p?: Record<string, never>
      ) => Promise<Result<ToolListItem[], ApiErr>>;
      listProviders: (
        _p?: Record<string, never>
      ) => Promise<Result<Array<{ key: string; displayName: string }>, ApiErr>>;
      listToolsForProvider: (p: {
        provider: string;
      }) => Promise<Result<ToolListItem[], ApiErr>>;
      getToolCredentialInfo: (p: {
        toolKey: string;
      }) => Promise<Result<ToolCredentialInfoResponse, ApiErr>>;
      listWorkosDataProviders: (
        _p?: Record<string, never>
      ) => Promise<
        Result<
          Array<{ provider: string; status: 'connected' | 'not_connected' }>,
          ApiErr
        >
      >;
      getWorkosAuthorizationUrl: (p: {
        provider: string;
      }) => Promise<
        Result<
          { authorizationUrl?: string; alreadyConnected?: boolean },
          ApiErr
        >
      >;
      disconnectWorkosProvider: (p: {
        provider: string;
      }) => Promise<Result<{ success: boolean }, ApiErr>>;
      syncWorkosProviderAccount: (p: { provider: string }) => Promise<
        Result<
          {
            providerKeys: string[];
            created: number;
            updated: number;
            skipped: number;
          },
          ApiErr
        >
      >;
      getSlackBotInstallation: (
        _p?: Record<string, never>
      ) => Promise<Result<{ installed: boolean; teamId?: string }, ApiErr>>;
      disconnectSlackBotInstallation: (
        _p?: Record<string, never>
      ) => Promise<Result<{ success: boolean; removed: boolean }, ApiErr>>;
      getLinearBotInstallation: (
        _p?: Record<string, never>
      ) => Promise<
        Result<{ installed: boolean; organizationId?: string }, ApiErr>
      >;
      disconnectLinearBotInstallation: (
        _p?: Record<string, never>
      ) => Promise<Result<{ success: boolean; removed: boolean }, ApiErr>>;
      listLinearTeamAgents: (_p?: Record<string, never>) => Promise<
        Result<
          {
            teams: Array<{
              teamId: string;
              teamName: string;
              agentId: string | null;
            }>;
          },
          ApiErr
        >
      >;
      setLinearTeamAgent: (p: {
        teamId: string;
        agentId: string;
      }) => Promise<Result<{ success: boolean }, ApiErr>>;
      clearLinearTeamAgent: (p: {
        teamId: string;
      }) => Promise<Result<{ success: boolean; removed: boolean }, ApiErr>>;
      listSlackChannelAgents: (_p?: Record<string, never>) => Promise<
        Result<
          {
            channels: Array<{
              channelId: string;
              channelName: string;
              agentId: string | null;
            }>;
          },
          ApiErr
        >
      >;
      setSlackChannelAgent: (p: {
        channelId: string;
        agentId: string;
        confirmShare?: boolean;
      }) => Promise<
        Result<
          { status: 'assigned' } | { status: 'needs-share'; agentId: string },
          ApiErr
        >
      >;
      clearSlackChannelAgent: (p: {
        channelId: string;
      }) => Promise<Result<{ success: boolean; removed: boolean }, ApiErr>>;
      getTeamsBotInstallation: (
        _p?: Record<string, never>
      ) => Promise<Result<{ installed: boolean; tenantId?: string }, ApiErr>>;
      listTeamsChannelAgents: (_p?: Record<string, never>) => Promise<
        Result<
          {
            channels: Array<{
              channelId: string;
              channelName: string;
              agentId: string | null;
            }>;
          },
          ApiErr
        >
      >;
      setTeamsChannelAgent: (p: {
        channelId: string;
        agentId: string;
        confirmShare?: boolean;
      }) => Promise<
        Result<
          { status: 'assigned' } | { status: 'needs-share'; agentId: string },
          ApiErr
        >
      >;
      clearTeamsChannelAgent: (p: {
        channelId: string;
      }) => Promise<Result<{ success: boolean; removed: boolean }, ApiErr>>;
      listProviderAccounts: (p: {
        provider: string;
      }) => Promise<
        Result<
          { provider: string; accounts: ProviderAccountListItem[] },
          ApiErr
        >
      >;
      listAllProviders: (
        _p?: Record<string, never>
      ) => Promise<Result<{ providers: ProviderWithAccounts[] }, ApiErr>>;
      createProviderAccount: (p: {
        toolKey: string;
        strategyName: string;
        displayName?: string;
        credentials: Record<string, string>;
      }) => Promise<
        Result<{ id: string; provider: string; displayName: string }, ApiErr>
      >;
      deleteProviderAccount: (p: {
        accountId: string;
      }) => Promise<Result<{ success: boolean }, ApiErr>>;
    };
    webhookProviders: {
      list: (
        _p?: Record<string, never>
      ) => Promise<Result<WebhookProviderCatalogOutput, ApiErr>>;
      connectCredentials: (p: {
        providerKey: WorkflowWebhookProviderKey;
        credentials: Record<string, string>;
      }) => Promise<
        Result<{ connected: boolean; providerKey: string }, ApiErr>
      >;
    };
    warehouse: {
      getPrimary: (
        _p?: Record<string, never>
      ) => Promise<Result<PrimaryWarehouse | null, ApiErr>>;
      irForHash: (p: {
        sqlHash: string;
      }) => Promise<Result<unknown | null, ApiErr>>;
      runMaterialization: (p: {
        viewName: string;
        schema: string;
      }) => Promise<Result<Record<string, never>, ApiErr>>;
      snapshotEventStream: (p: {
        streamSpecifier: StreamSpecifier;
      }) => Promise<Result<Record<string, never>, ApiErr>>;
      connection: {
        create: (p: {
          displayName: string;
          credentials: unknown;
          skipSetup?: boolean;
        }) => Promise<Result<unknown, ApiErr>>;
        createTable: (p: {
          tableName: string;
          headerRow: string[];
          dataRows: unknown[][];
          columnTypes: Record<string, unknown>;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
      };
    };
    atc: {
      getStackFramesForRunKey: (p: {
        runKey: string;
      }) => Promise<Result<GetStackFramesForRunKeyResponse | null, ApiErr>>;
      getStackFramesForRunKeyPreview: (p: {
        runKey: string;
      }) => Promise<
        Result<GetStackFramesForRunKeyPreviewResponse | null, ApiErr>
      >;
      retryRunKey: (p: {
        runKey: string;
      }) => Promise<Result<{ count: number }, ApiErr>>;
      cancelRunKey: (p: {
        runKey: string;
      }) => Promise<Result<{ count: number }, ApiErr>>;
      runColumnValue: (p: {
        config: LlmColConfigV2;
        relIdentity: string;
        colIdentity: string;
        stableId: string;
        completionId: string | null;
        messages: LlmMessage[];
      }) => Promise<Result<{ runId: string; runKey: string }, ApiErr>>;
      runGroup: {
        forPrefix: (p: {
          prefix: string;
          page?: number;
          perPage?: number;
          status: 'pending' | 'failed' | 'completed';
        }) => Promise<Result<RunOrGroupStats[], ApiErr>>;
        atPrefix: (p: {
          prefix: string;
        }) => Promise<Result<RunOrGroupStats, ApiErr>>;
      };
      allocator: {
        getLimits: (
          _p?: Record<string, never>
        ) => Promise<Result<AllocatorLimit[], ApiErr>>;
        remainingRateLimitsByRule: (
          _p?: Record<string, never>
        ) => Promise<Result<RemainingRateLimit[], ApiErr>>;
      };
    };
    org: {
      getPlan: (_p?: Record<string, never>) => Promise<Result<OrgPlan, ApiErr>>;
      listMembers: (
        _p?: Record<string, never>
      ) => Promise<Result<{ members: OrgMember[] }, ApiErr>>;
    };
    billingSettings: {
      balanceInfo: (
        _p?: Record<string, never>
      ) => Promise<Result<{ balanceInfo: BalanceInfo | null }, ApiErr>>;
      getOrbExternalId: (
        _p?: Record<string, never>
      ) => Promise<Result<{ orbExternalId: string | null }, ApiErr>>;
      getBillingSettingsInfo: (
        _p?: Record<string, never>
      ) => Promise<
        Result<{ billingSettingsInfo: BillingSettingsInfo }, ApiErr>
      >;
      addCredits: (p: {
        amount: number;
      }) => Promise<Result<{ status: 'success' | 'failure' }, ApiErr>>;
      setTopUpConfig: (p: {
        config: { amount: number; threshold: number } | null;
      }) => Promise<Result<{ status: 'success' | 'failure' }, ApiErr>>;
      generateAddCreditCardSession: (p: {
        returnUrl: string;
      }) => Promise<Result<{ addCreditCardUrl: string }, ApiErr>>;
      generateUpdateCreditCardSession: (p: {
        returnUrl: string;
      }) => Promise<Result<{ updateCreditCardUrl: string }, ApiErr>>;
    };
    onboarding: {
      submitOrgSetup: (p?: {
        orgName?: string;
      }) => Promise<Result<{ orgId: string }, ApiErr>>;
      recordReferral: (p: {
        referralCode: string;
      }) => Promise<Result<'ok', ApiErr>>;
    };
    referrals: {
      getIsRefererPartner: (
        _p?: Record<string, never>
      ) => Promise<Result<{ isReferrerPartner: boolean }, ApiErr>>;
      getReferralPayments: (
        _p?: Record<string, never>
      ) => Promise<Result<{ referralPayments: ReferredPayment[] }, ApiErr>>;
    };
    resource: {
      get: (
        p: ResourceGetParams
      ) => Promise<Result<ResourceGetResponse, ApiErr>>;
      list: (
        p: ResourceListParams
      ) => Promise<Result<ResourceListOutput, ApiErr>>;
      createShareLink: (
        p: CreateResourceShareLinkParams
      ) => Promise<
        Result<{ link: unknown; token: string; url: string }, ApiErr>
      >;
      activateShareLink: (p: {
        token: string;
      }) => Promise<Result<ActivateShareLinkOutput, ApiErr>>;
      revokeShareLink: (p: {
        shareLinkId: string;
      }) => Promise<Result<Record<string, never>, ApiErr>>;
      listShareLinks: (p: {
        targetResourceType?: ResourceType;
        targetResourceId?: string;
      }) => Promise<Result<{ links: unknown[] }, ApiErr>>;
      setSharedWithOrganization: (p: {
        targetResourceType: ResourceType;
        targetResourceId: string;
        permission: 'view' | 'edit';
      }) => Promise<Result<Record<string, never>, ApiErr>>;
      removeSharedWithOrganization: (p: {
        type: ResourceType;
        id: string;
        /** Another org that opened a link to it. Default: your own. */
        granteeOrgId?: string;
      }) => Promise<Result<Record<string, never>, ApiErr>>;
      /**
       * Limit an item in a folder to the people added to it (`follows:
       * false`), or let the folder's sharing reach it again.
       */
      setFollowsFolderSharing: (p: {
        type: ResourceType;
        id: string;
        follows: boolean;
      }) => Promise<Result<Record<string, never>, ApiErr>>;
      getAccess: (p: {
        type: ResourceType;
        id: string;
      }) => Promise<Result<ResourceAccess, ApiErr>>;
      setUserPermission: (p: {
        type: ResourceType;
        id: string;
        userId: string;
        permission: 'view' | 'edit';
        /** Email them a link if this is a new invite. */
        notify?: { alsoSharing: number };
      }) => Promise<Result<{ notified: boolean }, ApiErr>>;
      /**
       * Invite someone outside the org: Coco emails them a link that only
       * opens for that address. `url` is for handing it over by hand when the
       * email didn't go out.
       */
      inviteByEmail: (p: {
        targets: Array<{ type: ResourceType; id: string }>;
        email: string;
        permission: 'view' | 'edit';
        /** Email them the link; otherwise it is only returned. Default true. */
        notify?: boolean;
      }) => Promise<
        Result<{ notified: boolean; token: string; url: string }, ApiErr>
      >;
      removeUserPermission: (p: {
        type: ResourceType;
        id: string;
        userId: string;
      }) => Promise<Result<Record<string, never>, ApiErr>>;
      listActiveShareLinks: (p: {
        type: ResourceType;
        id: string;
      }) => Promise<Result<{ links: ResourceActiveShareLink[] }, ApiErr>>;
      previewShareLink: (p: {
        token: string;
      }) => Promise<Result<ResourceSharePreview, ApiErr>>;
      archive: (p: {
        type: ResourceType;
        id: string;
      }) => Promise<Result<Record<string, never>, ApiErr>>;
      setEmoji: (p: {
        type: ResourceType;
        id: string;
        emoji: string | null;
      }) => Promise<Result<Record<string, never>, ApiErr>>;

      favorites: {
        list: () => Promise<Result<ResourceFavoritesResponse, ApiErr>>;
        set: (p: {
          type: ResourceType;
          id: string;
          favorite: boolean;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        /** The whole list in its new order; omitted favorites sort after it. */
        reorder: (p: {
          order: { type: ResourceType; id: string }[];
        }) => Promise<Result<Record<string, never>, ApiErr>>;
      };

      chat: {
        chatStatus: (p: {
          conversationId: string;
        }) => Promise<Result<ChatStatusResponse, ApiErr>>;
        insights: (p: {
          conversationId: string;
        }) => Promise<Result<ChatInsightsResponse, ApiErr>>;
        listMessages: (p: {
          conversationId: string;
          userId?: string;
        }) => Promise<Result<ListMessagesResponse, ApiErr>>;
        listParticipants: (p: {
          conversationId: string;
        }) => Promise<Result<ListParticipantsResponse, ApiErr>>;
        getConversationSnapshot: (p: {
          conversationId: string;
        }) => Promise<Result<GetConversationSnapshotResponse, ApiErr>>;
        subAgentSnapshot: (p: {
          conversationId: string;
        }) => Promise<Result<SubAgentSnapshotResponse, ApiErr>>;
        markMessagesAsRead: (p: {
          conversationId: string;
          messageIds?: string[];
        }) => Promise<Result<ChatSuccessResponse, ApiErr>>;
        markMessagesAsUnread: (p: {
          conversationId: string;
          messageIds?: string[];
        }) => Promise<Result<ChatSuccessResponse, ApiErr>>;
        addAction: (p: {
          conversationId: string;
          action: ChatAddAction;
        }) => Promise<Result<ChatSuccessResponse, ApiErr>>;
        runTool: (p: {
          model: string;
          conversationId: string;
          toolCallId: string;
          action: 'approve' | 'deny';
          reason?: string;
          skipResume?: boolean;
        }) => Promise<Result<ChatToolRunResponse, ApiErr>>;
        submitTool: (p: {
          conversationId: string;
          toolCallIds: string[];
          model: string;
          result: unknown;
        }) => Promise<Result<ChatToolSubmitResponse, ApiErr>>;
        runInterruptedTool: (p: {
          conversationId: string;
          toolCallId: string;
          model: string;
          input: Record<string, unknown>;
        }) => Promise<Result<ChatToolRunResponse, ApiErr>>;
        /**
         * Declares client-implemented tools on a conversation already in
         * flight, for tools discovered mid-run rather than sent with a message.
         */
        addInterruptableTools: (p: {
          conversationId: string;
          tools: ChatPostInterruptableTool[];
        }) => Promise<Result<ChatAddInterruptableToolsResponse, ApiErr>>;
        stopChat: (p: {
          conversationId: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        archive: (p: {
          conversationId: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        rename: (p: {
          conversationId: string;
          title: string;
        }) => Promise<Result<ChatSuccessResponse, ApiErr>>;
        compact: (p: {
          conversationId: string;
          model: string;
        }) => Promise<Result<ChatSuccessResponse, ApiErr>>;
        chat: (p: ChatParams) => Promise<Result<ChatPostResponse, ApiErr>>;
        step: (p: ChatParams) => Promise<Result<ChatPostResponse, ApiErr>>;
      };

      agent: {
        create: (p: {
          editorState: unknown;
          model: string;
          id: string;
          folderId?: string;
          emoji?: string | null;
        }) => Promise<Result<AgentOutput, ApiErr>>;
        update: (p: {
          id: string;
          editorState?: unknown;
          model?: string;
        }) => Promise<Result<AgentOutput, ApiErr>>;
        archive: (p: {
          id: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        getMentionedToolsStatus: (p: {
          id: string;
        }) => Promise<Result<GetMentionedToolsStatusResponse, ApiErr>>;
        chat: (p: {
          id: string;
          message: string;
          conversationId?: string;
          mode?: 'auto' | 'manual';
          systemSuffix?: string;
        }) => Promise<
          Result<{ success: true; conversationId: string }, ApiErr>
        >;
        chatBatch: (p: {
          id: string;
          items: {
            message: string;
            conversationId?: string;
            mode?: 'auto' | 'manual';
            systemSuffix?: string;
          }[];
        }) => Promise<
          Result<{ success: true; conversationIds: string[] }, ApiErr>
        >;
      };

      document: {
        create: (p: {
          editorState: unknown;
          id: string;
          folderId?: string;
        }) => Promise<Result<DocumentOutput, ApiErr>>;
        update: (p: {
          id: string;
          editorState?: unknown;
          description?: string | null;
          writtenAt?: Date | string;
        }) => Promise<Result<DocumentOutput, ApiErr>>;
        delete: (p: {
          id: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        archive: (p: {
          id: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
      };

      dataset: {
        createCsv: (p: {
          file: File;
          name?: string;
          folderId?: string;
        }) => Promise<Result<DatasetOutput, ApiErr>>;
        uploadCsv: (p: {
          file: File;
          name?: string;
          folderId?: string;
          /** Reports upload progress as a fraction in [0, 1]. */
          onUploadProgress?: (fraction: number) => void;
        }) => Promise<Result<DatasetOutput, ApiErr>>;
        /**
         * Creates the dataset a CSV is about to be uploaded into, before the
         * file is sent, so the caller can open it and watch the upload land on
         * it rather than waiting out the transfer with nothing to show. Send the
         * bytes with {@link uploadCsvBytes} afterwards.
         *
         * The id is minted by the caller; creating one that already exists
         * returns it untouched.
         */
        createCsvPending: (p: {
          id: string;
          name?: string;
          folderId?: string;
          originalFilename?: string;
          mimeType?: string;
          sizeBytes?: number;
          /**
           * When set, the CSV is created as a chat-scoped dataset: hidden from
           * the library and stamped with the chat it came from. Used by Coco
           * chat file uploads. Promote it with {@link promote}.
           */
          sourceChatId?: string;
        }) => Promise<Result<DatasetOutput, ApiErr>>;
        /**
         * Promotes a chat-scoped dataset (created from a file dropped into a
         * chat) into a real library dataset, optionally renaming it. After this
         * the dataset appears in the library like any other.
         */
        promote: (p: {
          id: string;
          name?: string;
        }) => Promise<Result<DatasetOutput, ApiErr>>;
        /**
         * The id is minted by the caller so a dataset can be opened before it
         * exists; creating one that already exists returns it untouched.
         */
        createManual: (
          p: DatasetManualCreateBody & { id: string }
        ) => Promise<Result<DatasetOutput, ApiErr>>;
        /**
         * Creates a warehouse dataset seeded from a SELECT, before the query is
         * run, so the caller can open it and watch the seed land — the same
         * shape as {@link createCsvPending}. The id is minted by the caller;
         * creating one that already exists returns it untouched.
         */
        createWarehouse: (
          p: DatasetWarehouseCreateBody & { id: string }
        ) => Promise<Result<DatasetOutput, ApiErr>>;
        /**
         * Re-runs a warehouse dataset's seed with a new SELECT — the retry a
         * failed seed offers. Resets it to `importing` and kicks the seed again.
         */
        reseedWarehouse: (
          p: DatasetWarehouseReseedBody & { id: string }
        ) => Promise<Result<DatasetOutput, ApiErr>>;
        /**
         * Runs change detection on a warehouse dataset now, topping up the
         * capped preview from the source if it holds fewer than the cap.
         */
        detectWarehouse: (p: {
          id: string;
        }) => Promise<Result<DatasetOutput, ApiErr>>;
        /**
         * Pushes a warehouse dataset's pending operations into the org
         * warehouse now, instead of waiting out the compaction debounce.
         */
        flushWarehouse: (p: {
          id: string;
        }) => Promise<Result<{ datasetId: string; flushed: boolean }, ApiErr>>;
        /** Replaces a manual dataset's schema and rows with an uploaded CSV. */
        importCsv: (p: {
          id: string;
          file: File;
        }) => Promise<Result<DatasetOutput, ApiErr>>;
        /**
         * Fills a CSV dataset from an uploaded file, keeping the dataset id and
         * everything pointing at it — whether that dataset is waiting on its
         * first file or already holds rows. Rows are renumbered, so on a
         * re-upload row identity only survives if `rowKeyColumns` is declared.
         */
        uploadCsvBytes: (p: {
          id: string;
          file: File;
          onUploadProgress?: (fraction: number) => void;
        }) => Promise<Result<DatasetOutput, ApiErr>>;
        /**
         * Uploads a non-tabular document (pdf/text/markdown/html/pptx) into a
         * chat's ingot. The bytes go straight to the ingot and are never inlined
         * into the message; the chat turn posts a `coco/upload-file` placeholder
         * when they land and Ingot parses them.
         */
        uploadChatFile: (p: {
          id: string;
          chatId: string;
          filename: string;
          mediaType: string;
          file: File;
          onUploadProgress?: (fraction: number) => void;
        }) => Promise<
          Result<{ id: string; fileId: string; status: string }, ApiErr>
        >;
        /**
         * Where to read an artifact's parquet from. `signed` points straight at
         * the bucket and is what the preview should register with DuckDB;
         * `proxied` means this deployment cannot sign, and the caller should
         * fall back to the API's own streaming route.
         */
        artifactUrl: (p: {
          id: string;
          artifactId: string;
          kind: 'artifacts' | 'workflow';
        }) => Promise<Result<DatasetArtifactUrl, ApiErr>>;
        listOperations: (p: {
          id: string;
          afterSequenceNumber?: number;
        }) => Promise<Result<DatasetOperationListResponseV20260601, ApiErr>>;
        appendOperations: (
          p: DatasetOperationBatchBodyV20260601 & { id: string }
        ) => Promise<Result<DatasetOperationBatchResponseV20260601, ApiErr>>;
        /**
         * Whether a candidate business key identifies every row, answered by
         * the dataset's own kind against the rows themselves. Ask before
         * declaring one through {@link updateSettings}.
         */
        checkRowKeys: (
          p: DatasetRowKeyCheckBodyV20260601 & { id: string }
        ) => Promise<Result<DatasetRowKeyCheckResponseV20260601, ApiErr>>;
        /**
         * How big the dataset is, against how much of it the artifact carries.
         * The preview cannot derive this: it only ever sees the artifact, so a
         * sampled dataset would report its sample as the whole thing.
         */
        rowCount: (p: {
          id: string;
        }) => Promise<Result<DatasetRowCountOutput, ApiErr>>;
        updateSettings: (
          p: DatasetSettingsBodyV20260601 & { id: string }
        ) => Promise<Result<DatasetOutput, ApiErr>>;
        /**
         * Upsert per-column display options (number format, decimals,
         * alignment, in-cell bar). Shared across everyone who opens the dataset;
         * only the columns passed are written.
         */
        updateColumnSettings: (
          p: DatasetColumnSettingsBodyV20260601 & { id: string }
        ) => Promise<Result<DatasetOutput, ApiErr>>;
        archive: (p: {
          id: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        /**
         * Re-evaluate the dataset against its watchers now, out of band from its
         * cadence — the same debounced evaluation a write or a cron tick kicks.
         */
        detect: (p: {
          id: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
      };

      workflow: {
        create: (
          p: WorkflowCreateBody & { id: string }
        ) => Promise<Result<WorkflowDefinition, ApiErr>>;
        update: (
          p: WorkflowUpdateBody & { id: string }
        ) => Promise<Result<WorkflowDefinition, ApiErr>>;
        delete: (p: {
          id: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        getScheduledAgentBundle: (p: {
          workflowId: string;
        }) => Promise<Result<GetScheduledAgentBundleResponse, ApiErr>>;
        updateScheduledAgentRun: (p: {
          id: string;
          name?: string;
          schedule?: string;
          timezone?: string;
          agentId?: string;
          prompt?: string;
        }) => Promise<Result<WorkflowDefinition, ApiErr>>;
        runScheduledAgentNow: (p: {
          id: string;
        }) => Promise<Result<{ eventId: string }, ApiErr>>;
        listRuns: (p: {
          workflowId: string;
          limit?: number;
          offset?: number;
          /** Comma-separated statuses to include; omit for all. */
          status?: string;
          /** ISO timestamp lower bound on created_at (inclusive). */
          createdAfter?: string;
          /** ISO timestamp upper bound on created_at (inclusive). */
          createdBefore?: string;
          /** Comma-separated trigger types to include; omit for all. */
          triggerType?: string;
        }) => Promise<
          Result<{ items: WorkflowRunSummary[]; total: number }, ApiErr>
        >;
        runStats: (p: {
          workflowId: string;
          /** Comma-separated statuses to include; omit for all. */
          status?: string;
          /** ISO timestamp lower bound on created_at (inclusive). */
          createdAfter?: string;
          /** ISO timestamp upper bound on created_at (inclusive). */
          createdBefore?: string;
          /** Comma-separated trigger types to include; omit for all. */
          triggerType?: string;
          /** Time bucket size for the series; defaults to 'day'. */
          bucket?: 'hour' | 'day';
        }) => Promise<Result<WorkflowRunStats, ApiErr>>;
        getRun: (p: {
          workflowId: string;
          runId: string;
        }) => Promise<Result<WorkflowRunDetail, ApiErr>>;
        getRunGraph: (p: {
          workflowId: string;
          runId: string;
        }) => Promise<Result<WorkflowRunGraph, ApiErr>>;
        getRunStepPayload: (p: {
          workflowId: string;
          runId: string;
          nodeId: string;
        }) => Promise<Result<WorkflowRunStepPayload, ApiErr>>;
        /** Stops an in-progress run. No-op (`cancelled: false`) if already terminal. */
        cancelRun: (p: {
          workflowId: string;
          runId: string;
        }) => Promise<Result<WorkflowCancelRunResponse, ApiErr>>;
        /**
         * Replays a terminal run's own definition snapshot with its original
         * input as a NEW run; the run being retried is left untouched.
         */
        retryRun: (p: {
          workflowId: string;
          runId: string;
        }) => Promise<Result<WorkflowRetryRunResponse, ApiErr>>;
        /**
         * Releases what a debounce node is holding for the key this step was
         * suppressed on, so the next evaluation lets it through. The node and
         * the key are both resolved server-side from the step.
         */
        clearDebounce: (p: {
          workflowId: string;
          runId: string;
          nodeId: string;
        }) => Promise<Result<WorkflowClearDebounceResponse, ApiErr>>;
        /**
         * Completes a step a tool parked awaiting an external continuation and
         * resumes the run — the workflow-side counterpart of chat's
         * `runInterruptedTool`. `resumed: false` when the step was not parked.
         */
        resumeRunStep: (p: {
          workflowId: string;
          runId: string;
          nodeId: string;
          output: Record<string, unknown>;
          instanceKey?: string;
        }) => Promise<Result<WorkflowResumeStepResponse, ApiErr>>;
        listTriggers: (p: {
          workflowId: string;
        }) => Promise<Result<{ triggers: WorkflowTriggerView[] }, ApiErr>>;
        /**
         * Adds a trigger of any kind. `secret` comes back for webhooks only —
         * they are the only kind that mints one.
         */
        addTrigger: (
          p: { workflowId: string } & WorkflowAddTriggerBody
        ) => Promise<
          Result<
            { bindingId: string; triggerId: string; secret?: string },
            ApiErr
          >
        >;
        updateTrigger: (p: {
          workflowId: string;
          bindingId: string;
          triggerId?: string;
          name?: string;
          schedule?: string;
          timezone?: string;
          bindingEnabled?: boolean;
          cronRowEnabled?: boolean;
          webhookRowEnabled?: boolean;
          eventRowEnabled?: boolean;
          providerKey?: WorkflowWebhookProviderKey;
          verificationConfig?: unknown;
          eventKey?: string;
          eventConfig?: unknown;
          endpointSecret?: string;
          eventName?: string;
          priority?: number;
          defaultInput?: unknown;
          /** Dataset triggers only: `manual` never fires on its own. */
          runMode?: DatasetTriggerRunMode;
        }) => Promise<Result<{ ok: true }, ApiErr>>;
        renameTrigger: (p: {
          triggerId: string;
          name: string;
        }) => Promise<Result<{ ok: true }, ApiErr>>;
        getWebhookTriggerSettings: (p: {
          triggerId: string;
        }) => Promise<Result<WebhookTriggerSettings, ApiErr>>;
        /**
         * Fires a trigger by hand, whatever its kind — the server reads the
         * kind off the trigger. Produces real runs; only the idempotency
         * ledger is skipped, so this repeats on demand.
         *
         * `rowIds` is required for dataset triggers and ignored otherwise;
         * `payload` applies to event triggers. `workflowIds` narrows the
         * fan-out for every kind — without it, firing a trigger runs every
         * workflow bound to it.
         */
        triggerNow: (
          p: { triggerId: string } & WorkflowTriggerNowBody
        ) => Promise<
          Result<
            {
              dispatchedWorkflowRuns: number;
              runs: { runId: string; workflowId: string }[];
            },
            ApiErr
          >
        >;
        /** Which automations watch a dataset, and what each one writes back. */
        datasetAutomations: (p: { datasetId: string }) => Promise<
          Result<
            {
              triggers: {
                id: string;
                name: string | null;
                criterion: string;
                /** `manual` only ever fires from this dataset's Run menu. */
                runMode: DatasetTriggerRunMode;
                enabled: boolean;
                brokenReason: string | null;
                /**
                 * Whether this reader may fire the trigger, or only watch it.
                 * False for the sharer's own triggers on a dataset shared from
                 * another org.
                 */
                canRun: boolean;
                workflows: {
                  id: string;
                  name: string;
                  status: string;
                  bindingEnabled: boolean;
                  writes: { columnName: string; type: string }[];
                }[];
              }[];
              /**
               * The org that owns the dataset, when that differs from the
               * reader's own org. Null on an unshared dataset.
               */
              ownerOrgName: string | null;
            },
            ApiErr
          >
        >;
        /**
         * Per-row run state for a dataset's automations, one record per
         * (row, workflow). Rows that never ran are absent.
         */
        datasetAutomationOverlay: (p: {
          datasetId: string;
          /** Narrows the result to rows this artifact contains. */
          artifactId?: string | null;
        }) => Promise<
          Result<
            {
              rows: {
                datasetRowId: string | null;
                rowKey: string;
                workflowId: string;
                triggerId: string;
                runId: string;
                status: string;
                upToDate: boolean | null;
                ranAt: string | null;
                finishedAt: string | null;
                outputs: Record<string, unknown> | null;
                finalOutput: string | null;
              }[];
              truncated: boolean;
            },
            ApiErr
          >
        >;
        /**
         * Stops a bulk run and cancels the runs it already queued. Named by the
         * group id the caller minted and stamped on every run it started.
         *
         * Server-side because a batch can hold thousands of runs, which is not
         * a loop of per-run cancels a tab can be trusted to finish.
         */
        cancelDatasetRunGroup: (p: {
          datasetId: string;
          groupId: string;
        }) => Promise<Result<{ stopped: boolean }, ApiErr>>;
        /**
         * Records rows a bulk run means to fire, without firing them.
         *
         * Sent a page at a time because that is how this tab has them: the grid
         * evaluates its filters in DuckDB and reads matching ids out of its own
         * snapshot a page at a time. Nothing runs until `startDatasetRunGroup`,
         * so abandoning this half way fires nothing at all.
         */
        collectDatasetRunGroupRows: (p: {
          datasetId: string;
          groupId: string;
          triggerId: string;
          rowKeys: string[];
          workflowIds?: string[];
        }) => Promise<Result<{ added: number; collected: number }, ApiErr>>;
        /**
         * Says the set is complete and the batch should run.
         *
         * Everything after this belongs to the server, including closing the
         * batch when the last row has fired — which is why there is no way to
         */
        startDatasetRunGroup: (p: {
          datasetId: string;
          groupId: string;
          triggerId: string;
          workflowIds?: string[];
        }) => Promise<Result<{ rows: number; started: boolean }, ApiErr>>;
        /**
         * The batches on a dataset, newest first.
         *
         * What lets this tab pick up a bulk run it did not start — after a
         * reload, or one a colleague is running.
         */
        datasetRunGroups: (p: {
          datasetId: string;
          activeOnly?: boolean;
          limit?: number;
        }) => Promise<Result<{ groups: DatasetRunGroupProgress[] }, ApiErr>>;
        /**
         * Replays a whole dataset through its bound workflows, server-side.
         *
         * Carries no row ids on purpose — that is what `triggerNow` is for, and
         * for a whole dataset it would mean this tab holding the id set and
         * driving the loop. `replayedPairs` is zero when nothing was replayed.
         */
        replayDataset: (p: {
          datasetId: string;
          groupId: string;
          workflowIds?: string[];
          /** Skip rows whose latest run is current. */
          onlyNeedsRun: boolean;
        }) => Promise<Result<{ replayedPairs: number }, ApiErr>>;
        /** Retries every failed run across the dataset's bound workflows. */
        retryFailedDatasetRuns: (p: {
          datasetId: string;
          workflowIds?: string[];
        }) => Promise<
          Result<
            {
              retried: number;
              skipped: number;
              failedFound: number;
              truncated: boolean;
            },
            ApiErr
          >
        >;
        /** Emit-event names declared across the org, with their payload fields. */
        listEvents: (
          _p?: Record<string, never>
        ) => Promise<
          Result<
            { events: { eventName: string; payloadFields: string[] }[] },
            ApiErr
          >
        >;
        draftTestRun: (p: {
          workflowId: string;
          graph: WorkflowCreateBody['graph'];
          runInput?: unknown;
          name?: string;
        }) => Promise<
          Result<
            {
              runId: string;
              workflowId: string;
              ephemeralWorkflowId: string;
            },
            ApiErr
          >
        >;
        trigger: (p: {
          workflowId: string;
          runInput?: unknown;
        }) => Promise<Result<{ runId: string; workflowId: string }, ApiErr>>;
        bindTrigger: (p: {
          workflowId: string;
          triggerId: string;
          priority?: number;
          defaultInput?: unknown;
          enabled?: boolean;
        }) => Promise<
          Result<{ bindingId: string; triggerType: string }, ApiErr>
        >;
        listWorkflowsForTrigger: (p: { triggerId: string }) => Promise<
          Result<
            {
              workflows: {
                workflowId: string;
                name: string;
                status: string;
                bindingEnabled: boolean;
                lastRunAt: string | null;
                lastRunStatus: string | null;
                folderName?: string | null;
                folderScope?: 'user' | 'org' | 'external' | null;
              }[];
            },
            ApiErr
          >
        >;
        /**
         * Runs for many workflows in one window, in one request. The per-workflow
         * `listRuns` needs a request per workflow, which does not survive a page
         * that plots every schedule at once.
         */
        runsByWorkflows: (p: {
          workflowIds: string[];
          /** ISO timestamp lower bound on created_at (inclusive). */
          createdAfter?: string;
          /** ISO timestamp upper bound on created_at (inclusive). */
          createdBefore?: string;
          /** Cap on rows returned across all workflows combined. */
          limit?: number;
        }) => Promise<
          Result<{ items: WorkflowRunSummary[]; truncated: boolean }, ApiErr>
        >;
        summariesByIds: (p: { workflowIds: string[] }) => Promise<
          Result<
            {
              summaries: {
                workflowId: string;
                triggers: {
                  triggerId: string;
                  name: string;
                  schedule: string | null;
                  kind: string;
                  sharedCount: number;
                }[];
                lastRunAt: string | null;
                lastRunStatus: string | null;
              }[];
            },
            ApiErr
          >
        >;
        triggerSummariesByIds: (p: { triggerIds: string[] }) => Promise<
          Result<
            {
              summaries: {
                triggerId: string;
                workflowCount: number;
                lastFiredAt: string | null;
                hasError: boolean;
                workflows: {
                  workflowId: string;
                  name: string;
                  lastRunStatus: string | null;
                  failedRuns: number;
                  totalRuns: number;
                }[];
              }[];
            },
            ApiErr
          >
        >;
      };

      folder: {
        create: (p: {
          name: string;
          parentId?: string | null;
          scope: 'user' | 'org';
        }) => Promise<Result<FolderTreeFolder, ApiErr>>;
        rename: (p: {
          folderId: string;
          name: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        move: (p: {
          folderId: string;
          newParentId: string | null;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        archive: (p: {
          folderId: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        assignToFolder: (p: {
          folderId: string;
          entityType: ResourceType;
          entityId: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        unassignFromFolder: (p: {
          entityType: ResourceType;
          entityId: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        getDescendantFolderIds: (p: {
          folderId: string;
        }) => Promise<Result<{ folderIds: string[] }, ApiErr>>;
        getFolderItems: (p: {
          folderId: string;
          folderScope?: 'private' | 'shared';
          entityType?: ResourceType;
          itemTypes?: ResourceType[];
          search?: string;
          sort?: string;
          sortDir?: 'asc' | 'desc';
          limit?: number;
          offset?: number;
          unreadOnly?: boolean;
          triggerSources?: ResourceListParams['triggerSources'];
        }) => Promise<Result<FolderItemsResponse, ApiErr>>;
        /** Top-level Library: root folders + unfiled artifacts, filtered + paged. */
        getLibraryRoot: (p?: {
          ownership?: 'mine' | 'shared';
          search?: string;
          sort?: string;
          sortDir?: 'asc' | 'desc';
          limit?: number;
          offset?: number;
        }) => Promise<Result<FolderItemsResponse, ApiErr>>;
        getUnfiledTotals: (p?: {
          folderScope?: 'private' | 'shared';
        }) => Promise<Result<FolderUnfiledTotals, ApiErr>>;
        moveToFolder: (p: {
          type: ResourceType;
          id: string;
          folderId: string;
        }) => Promise<Result<Record<string, never>, ApiErr>>;
        getOrganizeDraft: (p: {
          conversationId: string;
        }) => Promise<Result<OrganizeDraftResponse, ApiErr>>;
        createOrganizeDraft: (p: {
          conversationId: string;
          /** 'shared' organizes the org's shared library (admin-only). */
          mode?: 'personal' | 'shared';
        }) => Promise<Result<OrganizeDraftResponse, ApiErr>>;
        /**
         * The Library "Organize" button: reuse the open organize chat for this
         * library (or mint one), seed the draft, and persist the artifact onto the
         * conversation. Returns the conversation id to navigate to.
         */
        openOrganize: (p: {
          mode?: 'personal' | 'shared';
        }) => Promise<Result<OrganizeOpenResponse, ApiErr>>;
        addOrganizeDraftFolder: (p: {
          conversationId: string;
          name?: string;
        }) => Promise<Result<OrganizeDraftResponse, ApiErr>>;
        renameOrganizeDraftFolder: (p: {
          conversationId: string;
          draftId: string;
          name: string;
        }) => Promise<Result<OrganizeDraftResponse, ApiErr>>;
        removeOrganizeDraftFolder: (p: {
          conversationId: string;
          draftId: string;
        }) => Promise<Result<OrganizeDraftResponse, ApiErr>>;
        moveOrganizeDraftFolder: (p: {
          conversationId: string;
          draftId: string;
          parentDraftId: string | null;
        }) => Promise<Result<OrganizeDraftResponse, ApiErr>>;
        moveOrganizeDraftMember: (p: {
          conversationId: string;
          member: { type: ResourceType; id: string };
          toDraftId: string;
        }) => Promise<Result<OrganizeDraftResponse, ApiErr>>;
        removeOrganizeDraftMember: (p: {
          conversationId: string;
          member: { type: ResourceType; id: string };
        }) => Promise<Result<OrganizeDraftResponse, ApiErr>>;
        commitOrganizeDraft: (p: {
          conversationId: string;
          excludeDraftIds?: string[];
        }) => Promise<Result<OrganizeCommitResponse, ApiErr>>;
      };
    };
    playground: {
      /** URL for the legacy streaming chat transport (DefaultChatTransport). */
      chatUrl: string;
      /**
       * Authenticated fetch for the streaming chat transport. Injects the
       * bearer token and retries once on 401 with a forced token refresh,
       * matching the behavior of the JSON endpoints on this client.
       */
      chatFetch: typeof fetch;
    };
    devServer: {
      manifests: {
        manifestMeta: (
          _p?: Record<string, never>
        ) => Promise<Result<ManifestMeta, ApiErr>>;
        definition: (p: {
          id: string;
          version?: string;
        }) => Promise<Result<AST.RelFR | null, ApiErr>>;
      };
    };
    workosWidget: {
      /** Mint a scoped token for the User Management widget (admin-only). */
      usersTableToken: (
        _p?: Record<string, never>
      ) => Promise<Result<{ token: string }, ApiErr>>;
    };
  };

  // Untenanted namespace (.u.)
  u: {
    whoami: {
      get: (_p?: Record<string, never>) => Promise<Result<Whoami, ApiErr>>;
    };
    onboarding: {
      submitOrgSetup: (p?: {
        orgName?: string;
      }) => Promise<Result<{ orgId: string }, ApiErr>>;
      recordReferral: (p: {
        referralCode: string;
      }) => Promise<Result<'ok', ApiErr>>;
    };
    atcVisibility: {
      setAtcRunVisibility: (p: {
        runKey: string;
        visible: boolean;
      }) => Promise<Result<{ runKey: string; visible: boolean }, ApiErr>>;
      getOrgsByIds: (p: {
        orgIds: string[];
      }) => Promise<Result<{ id: string; name: string }[], ApiErr>>;
      getRestateUrl: (
        p: Record<string, never>
      ) => Promise<Result<{ restateUrl: string | null }, ApiErr>>;
      getStatusSummary: (p: { timeInterval: string }) => Promise<
        Result<
          Array<{
            service: string;
            total: number;
            successCount: number;
            failureCount: number;
            percentSuccess: number;
          }>,
          ApiErr
        >
      >;
      getFailureTypeSummary: (p: { timeInterval: string }) => Promise<
        Result<
          Array<{
            failureType: string;
            count: number;
            service: string;
            exampleOrgId: string;
            exampleRunId: string;
            exampleId: string;
            orgBreakdown: { orgId: string; count: number }[];
          }>,
          ApiErr
        >
      >;
      getFailureTypeDetails: (p: { orgId: string; runId: string }) => Promise<
        Result<
          {
            model: string | null;
            agentId: string | null;
            tools: string | null;
            orgId: string;
            organizationName: string | null;
          } | null,
          ApiErr
        >
      >;
      getSagaInvocations: (p: {
        timeInterval: string;
        includeSagaType?: string;
        excludeSagaTypes?: string[];
      }) => Promise<
        Result<
          Array<{
            id: string;
            status: string;
            targetServiceKey: string;
            createdAt: string;
            completionResult: string | null;
            completionFailure: string | null;
            orgId: string | null;
            sagaId: string | null;
            sagaType: string | null;
            entityName: string | null;
          }>,
          ApiErr
        >
      >;
      getWebhookTriggers: (p: { timeInterval: string }) => Promise<
        Result<
          {
            webhooks: Array<{
              webhookId: string;
              name: string;
              ownerOrgId: string;
              organizationName: string | null;
              externalSubscriptionType: string | null;
              providerKey: string;
              installStatus: string;
              enabled: boolean;
              createdAt: string;
            }>;
            runs: Array<{
              id: string;
              webhookId: string;
              status: string;
              createdAt: string;
              completionResult: string | null;
              completionFailure: string | null;
            }>;
            registryTruncated: boolean;
          },
          ApiErr
        >
      >;
      retrySagaInvocation: (p: {
        invocationId: string;
      }) => Promise<
        Result<
          { newSagaId: string; sagaType: string; entityName: string | null },
          ApiErr
        >
      >;
      restartAtcFailuresAsNew: (p: {
        timeInterval: string;
        failureType: string;
        orgId: string;
      }) => Promise<
        Result<
          { succeeded: number; failed: number; sampleError?: string },
          ApiErr
        >
      >;
      getInvocationSummary: (p: { timeInterval: string }) => Promise<
        Result<
          Array<{
            org: string;
            service: string;
            successCount: number;
            failureCount: number;
            percFailure: string;
            total: number;
          }>,
          ApiErr
        >
      >;
      getActiveInvocationsByOrg: (
        p: Record<string, never>
      ) => Promise<Result<Array<{ org: string; activeCount: number }>, ApiErr>>;
      killOrgInvocations: (p: {
        orgId: string;
      }) => Promise<Result<{ succeeded: number; failed: number }, ApiErr>>;
      getStackFramesBreakdown: (p: { timeInterval: string }) => Promise<
        Result<
          Array<{
            orgId: string;
            entity: string | null;
            key: string | null;
            percUnfinished: number;
            totalUnfinished: number;
            totalRun: number;
            percFailed: number;
            percNoResultMessage: number;
            percNoStackFrameEvents: number;
            percPending: number;
            totalFailed: number;
            totalNoResultMessage: number;
            totalNoStackFrameEvents: number;
            totalPending: number;
            totalOtherStatus: number;
          }>,
          ApiErr
        >
      >;
      executeCustomQuery: (p: {
        query: string;
      }) => Promise<Result<{ rows: Record<string, unknown>[] }, ApiErr>>;
    };
    superAdminBilling: {
      addOrbCredit: (p: {
        orgId: string;
        amount: number;
        memo: string;
      }) => Promise<Result<Record<string, never>, ApiErr>>;
      billEngineeringHours: (p: {
        orgId: string;
        hours: number;
        externalNote: string;
        internalNote?: string;
      }) => Promise<Result<Record<string, never>, ApiErr>>;
    };
    cli: {
      getDeviceCodeStatus: (p: {
        deviceCode: string;
      }) => Promise<
        Result<
          { device_code: string; approved: boolean; expires_at: string },
          ApiErr
        >
      >;
      approve: (p: { deviceCode: string }) => Promise<
        Result<
          {
            approved: boolean;
            user_id?: string;
            email?: string;
            message?: string;
          },
          ApiErr
        >
      >;
      getStatus: (
        _p?: Record<string, never>
      ) => Promise<Result<{ connected: boolean; userId: string }, ApiErr>>;
    };
    transcribe: {
      audio: (p: {
        formData: FormData;
      }) => Promise<Result<{ text: string }, ApiErr>>;
    };
  };
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function makeTypedClient(config: TypedClientConfig): CoteraApiV2 {
  return {
    t: {
      // -----------------------------------------------------------------------
      // commits
      // -----------------------------------------------------------------------
      commits: {
        list: () => get(config, 'commits/list'),
        commit: (p) =>
          post(
            config,
            'commits/commit',
            p,
            withApiSchema(CommitsCommitV20260601Schema.version)
          ),
      },

      // -----------------------------------------------------------------------
      // artifacts
      // -----------------------------------------------------------------------
      artifacts: {
        create: (p) =>
          post(
            config,
            'artifacts',
            p,
            withApiSchema(ArtifactsCreateV20260601Schema.version)
          ),
        createByDefinitionId: (p) =>
          post(config, `artifacts/definitions/${enc(p.defId)}`),
        getRequestById: (p) => get(config, `artifacts/requests/${enc(p.id)}`),
        listRequests: (p) =>
          get(config, 'artifacts/requests', {
            sqlHash: p.sqlHash,
            pagination:
              p.pagination !== undefined
                ? JSON.stringify(p.pagination)
                : undefined,
          }),
        usableCache: () => get(config, 'artifacts/cache'),
        invalidateArtifact: (p) =>
          post(config, `artifacts/${enc(p.id)}/invalidate`),
      },

      // -----------------------------------------------------------------------
      // tools (legacy)
      // -----------------------------------------------------------------------
      tools: {
        list: () => get(config, 'tools/list'),
        listConnections: () => get(config, 'tools/list-connections'),
        updateToolsConnection: (p) =>
          post(
            config,
            'tools/update-tools-connection',
            p,
            withApiSchema(ToolsUpdateToolsConnectionV20260601Schema.version)
          ),
        execute: (p) =>
          post(
            config,
            'tools/execute',
            p,
            withApiSchema(ToolsExecuteV20260601Schema.version)
          ),
      },

      // -----------------------------------------------------------------------
      // toolsV2
      // -----------------------------------------------------------------------
      toolsV2: {
        list: () => get(config, 'tools-v2'),
        listProviders: () => get(config, 'tools-v2/providers'),
        listToolsForProvider: (p) =>
          get(config, `tools-v2/providers/${enc(p.provider)}/tools`),
        getToolCredentialInfo: (p) =>
          get(config, `tools-v2/tools/${enc(p.toolKey)}/credentials`),
        listWorkosDataProviders: () => get(config, 'tools-v2/workos/providers'),
        getWorkosAuthorizationUrl: (p) =>
          post(
            config,
            `tools-v2/workos/providers/${enc(p.provider)}/authorize`
          ),
        disconnectWorkosProvider: (p) =>
          post(
            config,
            `tools-v2/workos/providers/${enc(p.provider)}/disconnect`
          ),
        syncWorkosProviderAccount: (p) =>
          post(config, `tools-v2/workos/providers/${enc(p.provider)}/sync`),
        getSlackBotInstallation: () =>
          get(config, 'tools-v2/slack/installation'),
        disconnectSlackBotInstallation: () =>
          post(config, 'tools-v2/slack/installation/disconnect'),
        getLinearBotInstallation: () =>
          get(config, 'tools-v2/linear/installation'),
        disconnectLinearBotInstallation: () =>
          post(config, 'tools-v2/linear/installation/disconnect'),
        listLinearTeamAgents: () => get(config, 'tools-v2/linear/team-agents'),
        setLinearTeamAgent: (p) =>
          post(config, `tools-v2/linear/team-agents/${enc(p.teamId)}/set`, {
            agentId: p.agentId,
          }),
        clearLinearTeamAgent: (p) =>
          post(config, `tools-v2/linear/team-agents/${enc(p.teamId)}/clear`),
        listSlackChannelAgents: () =>
          get(config, 'tools-v2/slack/channel-agents'),
        setSlackChannelAgent: (p) =>
          post(
            config,
            `tools-v2/slack/channel-agents/${enc(p.channelId)}/set`,
            {
              agentId: p.agentId,
              confirmShare: p.confirmShare ?? false,
            }
          ),
        clearSlackChannelAgent: (p) =>
          post(
            config,
            `tools-v2/slack/channel-agents/${enc(p.channelId)}/clear`
          ),
        getTeamsBotInstallation: () =>
          get(config, 'tools-v2/teams/installation'),
        listTeamsChannelAgents: () =>
          get(config, 'tools-v2/teams/channel-agents'),
        setTeamsChannelAgent: (p) =>
          post(
            config,
            `tools-v2/teams/channel-agents/${enc(p.channelId)}/set`,
            {
              agentId: p.agentId,
              confirmShare: p.confirmShare ?? false,
            }
          ),
        clearTeamsChannelAgent: (p) =>
          post(
            config,
            `tools-v2/teams/channel-agents/${enc(p.channelId)}/clear`
          ),
        listProviderAccounts: (p) =>
          get(config, `tools-v2/providers/${enc(p.provider)}/accounts`),
        listAllProviders: () => get(config, 'tools-v2/providers/all'),
        createProviderAccount: (p) =>
          post(
            config,
            'tools-v2/accounts',
            p,
            withApiSchema(ToolsV2CreateAccountV20260601Schema.version)
          ),
        deleteProviderAccount: (p) =>
          post(config, `tools-v2/accounts/${enc(p.accountId)}/delete`),
      },

      // -----------------------------------------------------------------------
      // webhookProviders
      // -----------------------------------------------------------------------
      webhookProviders: {
        list: () => get(config, 'webhook-providers'),
        connectCredentials: (p) =>
          post(config, `webhook-providers/${enc(p.providerKey)}/credentials`, {
            credentials: p.credentials,
          }),
      },

      // -----------------------------------------------------------------------
      // warehouse
      // -----------------------------------------------------------------------
      warehouse: {
        getPrimary: () => get(config, 'warehouse/primary'),
        irForHash: (p) => get(config, `warehouse/ir/${enc(p.sqlHash)}`),
        runMaterialization: (p) =>
          post(
            config,
            'warehouse/materializations/run',
            p,
            withApiSchema(WarehouseRunMaterializationV20260601Schema.version)
          ),
        snapshotEventStream: (p) =>
          post(
            config,
            'warehouse/event-streams/snapshot',
            p,
            withApiSchema(WarehouseSnapshotEventStreamV20260601Schema.version)
          ),
        connection: {
          create: (p) =>
            post(
              config,
              'warehouse/connections',
              p,
              withApiSchema(WarehouseConnectionCreateV20260601Schema.version)
            ),
          createTable: (p) =>
            post(
              config,
              'warehouse/connections/tables',
              p,
              withApiSchema(
                WarehouseConnectionCreateTableV20260601Schema.version
              )
            ),
        },
      },

      // -----------------------------------------------------------------------
      // atc
      // -----------------------------------------------------------------------
      atc: {
        getStackFramesForRunKey: (p) =>
          get(config, `atc/run-keys/${enc(p.runKey)}/stack-frames`),
        getStackFramesForRunKeyPreview: (p) =>
          get(config, 'atc/run-keys/stack-frames/summary', {
            runKey: p.runKey,
          }),
        retryRunKey: (p) => post(config, `atc/run-keys/${enc(p.runKey)}/retry`),
        cancelRunKey: (p) =>
          post(config, `atc/run-keys/${enc(p.runKey)}/cancel`),
        runColumnValue: (p) =>
          post(
            config,
            'atc/column-values/run',
            p,
            withApiSchema(AtcRunColumnValueV20260601Schema.version)
          ),
        runGroup: {
          forPrefix: (p) =>
            get(config, `atc/run-groups/${enc(p.prefix)}`, {
              page: p.page,
              perPage: p.perPage,
              status: p.status,
            }),
          atPrefix: (p) => get(config, `atc/run-groups/${enc(p.prefix)}/at`),
        },
        allocator: {
          getLimits: () => get(config, 'atc/allocator/limits'),
          remainingRateLimitsByRule: () =>
            get(config, 'atc/allocator/rate-limits'),
        },
      },

      // -----------------------------------------------------------------------
      // org
      // -----------------------------------------------------------------------
      org: {
        getPlan: () => get(config, 'org/plan'),
        listMembers: () => get(config, 'org/members'),
      },

      // -----------------------------------------------------------------------
      // billingSettings
      // -----------------------------------------------------------------------
      billingSettings: {
        balanceInfo: () => get(config, 'billing/balance'),
        getOrbExternalId: () => get(config, 'billing/orb-external-id'),
        getBillingSettingsInfo: () => get(config, 'billing/settings'),
        addCredits: (p) =>
          post(
            config,
            'billing/credits',
            p,
            withApiSchema(BillingAddCreditsV20260601Schema.version)
          ),
        setTopUpConfig: (p) =>
          post(
            config,
            'billing/top-up',
            p,
            withApiSchema(BillingTopUpV20260601Schema.version)
          ),
        generateAddCreditCardSession: (p) =>
          post(
            config,
            'billing/sessions/add-card',
            p,
            withApiSchema(BillingAddCardSessionV20260601Schema.version)
          ),
        generateUpdateCreditCardSession: (p) =>
          post(
            config,
            'billing/sessions/update-card',
            p,
            withApiSchema(BillingUpdateCardSessionV20260601Schema.version)
          ),
      },

      // -----------------------------------------------------------------------
      // onboarding
      // -----------------------------------------------------------------------
      onboarding: {
        submitOrgSetup: (p) =>
          post(
            config,
            'onboarding/org-setup',
            p,
            withApiSchema(OnboardingOrgSetupV20260601Schema.version)
          ),
        recordReferral: (p) =>
          post(
            config,
            'onboarding/referral',
            p,
            withApiSchema(OnboardingReferralV20260601Schema.version)
          ),
      },

      // -----------------------------------------------------------------------
      // referrals
      // -----------------------------------------------------------------------
      referrals: {
        getIsRefererPartner: () => get(config, 'referrals/referrer/partner'),
        getReferralPayments: () => get(config, 'referrals/referrer/payments'),
      },

      // -----------------------------------------------------------------------
      // resource
      // -----------------------------------------------------------------------
      resource: {
        get: (p) => get(config, `resource/${enc(p.type)}/${enc(p.id)}`),
        list: (p) =>
          get(
            config,
            `resource/${enc(p.type)}`,
            {
              limit: p.limit,
              offset: p.offset,
              folderScope: p.folderScope,
              ownership: p.ownership,
              search: p.search,
              sort: p.sort,
              sortDir: p.sortDir,
              workflowScheduledAgentOnly: p.workflowScheduledAgentOnly,
              unreadOnly: p.unreadOnly,
              unfiledOnly: p.unfiledOnly,
              agentId: p.agentId,
              triggerSources: p.triggerSources,
              triggerKind: p.triggerKind,
              includeTotal: p.includeTotal,
            },
            withApiSchema(ResourceListV20260601Schema.version)
          ),
        createShareLink: (p) =>
          post(
            config,
            'resource/share-links',
            p,
            withApiSchema(ResourceCreateShareLinkV20260601Schema.version)
          ),
        activateShareLink: (p) =>
          post(
            config,
            'resource/share-links/activate',
            p,
            withApiSchema(ResourceActivateShareLinkV20260601Schema.version)
          ),
        revokeShareLink: (p) =>
          post(config, `resource/share-links/${enc(p.shareLinkId)}/revoke`),
        listShareLinks: (p) => {
          let path = 'resource/share-links';
          if (p.targetResourceType !== undefined) {
            path += `/${enc(p.targetResourceType)}`;
            if (p.targetResourceId !== undefined) {
              path += `/${enc(p.targetResourceId)}`;
            }
          }
          return get(config, path);
        },
        setSharedWithOrganization: (p) =>
          post(
            config,
            `resource/share-links/${enc(p.targetResourceType)}/${enc(
              p.targetResourceId
            )}/organization`,
            { permission: p.permission },
            withApiSchema(
              ResourceSetSharedWithOrganizationV20260601Schema.version
            )
          ),
        removeSharedWithOrganization: (p) =>
          post(
            config,
            `resource/share-links/${enc(p.type)}/${enc(
              p.id
            )}/organization/remove`,
            p.granteeOrgId === undefined
              ? {}
              : { granteeOrgId: p.granteeOrgId },
            withApiSchema(
              ResourceRemoveSharedWithOrganizationV20260925Schema.version
            )
          ),
        setFollowsFolderSharing: (p) =>
          post(
            config,
            `resource/share-links/${enc(p.type)}/${enc(p.id)}/folder-sharing`,
            { follows: p.follows },
            withApiSchema(
              ResourceSetFollowsFolderSharingV20260928Schema.version
            )
          ),
        getAccess: (p) =>
          get(
            config,
            `resource/share-links/${enc(p.type)}/${enc(p.id)}/access`,
            undefined,
            withApiSchema(ResourceGetAccessV20260925Schema.version)
          ),
        setUserPermission: (p) =>
          post(
            config,
            `resource/share-links/${enc(p.type)}/${enc(p.id)}/people`,
            {
              userId: p.userId,
              permission: p.permission,
              ...(p.notify !== undefined && { notify: p.notify }),
            },
            withApiSchema(ResourceSetUserPermissionV20260925Schema.version)
          ),
        inviteByEmail: (p) =>
          post(
            config,
            'resource/share-links/invite',
            p,
            withApiSchema(ResourceInviteByEmailV20260928Schema.version)
          ),
        removeUserPermission: (p) =>
          post(
            config,
            `resource/share-links/${enc(p.type)}/${enc(p.id)}/people/${enc(
              p.userId
            )}/remove`,
            {},
            withApiSchema(ResourceRemoveUserPermissionV20260925Schema.version)
          ),
        listActiveShareLinks: (p) =>
          get(
            config,
            `resource/share-links/${enc(p.type)}/${enc(p.id)}/links`,
            undefined,
            withApiSchema(ResourceListActiveShareLinksV20260925Schema.version)
          ),
        previewShareLink: (p) =>
          get(
            config,
            `resource/share-links/preview/${enc(p.token)}`,
            undefined,
            withApiSchema(ResourcePreviewShareLinkV20260925Schema.version)
          ),
        archive: (p) =>
          post(config, `resource/${enc(p.type)}/${enc(p.id)}/archive`),
        setEmoji: (p) =>
          post(
            config,
            `resource/${enc(p.type)}/${enc(p.id)}/emoji`,
            { emoji: p.emoji },
            withApiSchema(ResourceSetEmojiV20260601Schema.version)
          ),

        favorites: {
          list: () =>
            get(
              config,
              'resource/favorites',
              undefined,
              withApiSchema(ResourceListFavoritesV20260601Schema.version)
            ),
          set: (p) =>
            post(
              config,
              `resource/${enc(p.type)}/${enc(p.id)}/favorite`,
              { favorite: p.favorite },
              withApiSchema(ResourceSetFavoriteV20260601Schema.version)
            ),
          reorder: (p) =>
            post(
              config,
              'resource/favorites/reorder',
              { order: p.order },
              withApiSchema(ResourceReorderFavoritesV20260601Schema.version)
            ),
        },

        chat: {
          chatStatus: (p) =>
            get(config, `resource/chat/${enc(p.conversationId)}/status`),
          insights: (p) =>
            get(config, `resource/chat/${enc(p.conversationId)}/insights`),
          listMessages: (p) =>
            get(config, `resource/chat/${enc(p.conversationId)}/list`),
          listParticipants: (p) =>
            get(config, `resource/chat/${enc(p.conversationId)}/participants`),
          getConversationSnapshot: (p) =>
            get(config, `resource/chat/${enc(p.conversationId)}/snapshot`),
          subAgentSnapshot: (p) =>
            get(
              config,
              `resource/chat/${enc(p.conversationId)}/sub-agent-snapshot`
            ),
          markMessagesAsRead: (p) =>
            post(
              config,
              `resource/chat/${enc(p.conversationId)}/messages/read`,
              { messageIds: p.messageIds },
              withApiSchema(ChatMessagesReadV20260601Schema.version)
            ),
          markMessagesAsUnread: (p) =>
            post(
              config,
              `resource/chat/${enc(p.conversationId)}/messages/unread`,
              { messageIds: p.messageIds },
              withApiSchema(ChatMessagesUnreadV20260601Schema.version)
            ),
          addAction: (p) =>
            post(
              config,
              `resource/chat/${enc(p.conversationId)}/actions`,
              { action: p.action },
              withApiSchema(ChatActionsV20260601Schema.version)
            ),
          runTool: (p) =>
            post(
              config,
              `resource/chat/${enc(p.conversationId)}/tools/${enc(
                p.toolCallId
              )}/run`,
              {
                model: p.model,
                action: p.action,
                reason: p.reason,
                ...(p.skipResume === true && { skipResume: true }),
              },
              withApiSchema(ChatToolRunV20260601Schema.version)
            ),
          submitTool: (p) =>
            post(
              config,
              `resource/chat/${enc(p.conversationId)}/tools/submit`,
              {
                toolCallIds: p.toolCallIds,
                model: p.model,
                result: p.result,
              },
              withApiSchema(ChatToolSubmitV20260601Schema.version)
            ),
          runInterruptedTool: (p) =>
            post(
              config,
              `resource/chat/${enc(p.conversationId)}/tools/${enc(
                p.toolCallId
              )}/run-interrupted`,
              {
                model: p.model,
                input: p.input,
              },
              withApiSchema(ChatToolRunInterruptedV20260601Schema.version)
            ),
          addInterruptableTools: (p) =>
            post(
              config,
              `resource/chat/${enc(p.conversationId)}/interruptable-tools`,
              { tools: p.tools },
              withApiSchema(ChatAddInterruptableToolsV20260601Schema.version)
            ),
          stopChat: (p) =>
            post(config, `resource/chat/${enc(p.conversationId)}/stop`),
          archive: (p) =>
            post(config, `resource/chat/${enc(p.conversationId)}/archive`),
          rename: (p) =>
            post(
              config,
              `resource/chat/${enc(p.conversationId)}/rename`,
              { title: p.title },
              withApiSchema(ChatRenameV20260601Schema.version)
            ),
          compact: (p) =>
            post(
              config,
              `resource/chat/${enc(p.conversationId)}/compact`,
              { model: p.model },
              withApiSchema(ChatCompactV20260601Schema.version)
            ),
          chat: (p) => {
            const { conversationId, ...contextWithoutId } = p.context;
            return post(
              config,
              `resource/chat/${enc(conversationId)}`,
              {
                ...p,
                context: contextWithoutId,
              },
              withApiSchema(ChatPostV20260601Schema.version)
            );
          },
          step: (p) => {
            const { conversationId, ...contextWithoutId } = p.context;
            return post(
              config,
              `resource/chat/${enc(conversationId)}/step`,
              {
                ...p,
                context: contextWithoutId,
              },
              withApiSchema(ChatStepV20260601Schema.version)
            );
          },
        },

        agent: {
          create: (p) => {
            const { id, ...bodyWithoutId } = p;
            return post(
              config,
              `resource/agent/${enc(id)}`,
              bodyWithoutId,
              withApiSchema(AgentCreateV20260601Schema.version)
            );
          },
          update: (p) =>
            post(
              config,
              `resource/agent/${enc(p.id)}/update`,
              {
                editorState: p.editorState,
                model: p.model,
              },
              withApiSchema(AgentUpdateV20260601Schema.version)
            ),
          archive: (p) => post(config, `resource/agent/${enc(p.id)}/archive`),
          getMentionedToolsStatus: (p) =>
            get(config, `resource/agent/${enc(p.id)}/mentioned-tools-status`),
          chat: (p) => {
            const { id, ...bodyWithoutId } = p;
            return post(
              config,
              `resource/agent/${enc(id)}/chat`,
              bodyWithoutId,
              withApiSchema(AgentChatV20260601Schema.version)
            );
          },
          chatBatch: (p) => {
            const { id, items } = p;
            return post(
              config,
              `resource/agent/${enc(id)}/chat/batch`,
              items,
              withApiSchema(AgentChatBatchV20260601Schema.version)
            );
          },
        },

        document: {
          create: (p) => {
            const { id, ...bodyWithoutId } = p;
            return post(
              config,
              `resource/document/${enc(id)}`,
              bodyWithoutId,
              withApiSchema(DocumentCreateV20260601Schema.version)
            );
          },
          update: (p) =>
            post(
              config,
              `resource/document/${enc(p.id)}/update`,
              {
                editorState: p.editorState,
                description: p.description,
                writtenAt: p.writtenAt,
              },
              withApiSchema(DocumentUpdateV20260601Schema.version)
            ),
          delete: (p) => post(config, `resource/document/${enc(p.id)}/delete`),
          archive: (p) =>
            post(config, `resource/document/${enc(p.id)}/archive`),
        },

        dataset: {
          createCsv: (p) => {
            const formData = new FormData();
            formData.append('file', p.file, p.file.name);
            if (p.name !== undefined) {
              formData.append('name', p.name);
            }
            if (p.folderId !== undefined) {
              formData.append('folderId', p.folderId);
            }
            return postFormData(
              config,
              'resources/datasets/csv/create',
              formData,
              withApiSchema(DatasetCsvCreateV20260601Schema.version)
            );
          },
          uploadCsv: (p) => {
            const searchParams = new URLSearchParams();
            searchParams.set('originalFilename', p.file.name);
            if (p.name !== undefined) {
              searchParams.set('name', p.name);
            }
            if (p.folderId !== undefined) {
              searchParams.set('folderId', p.folderId);
            }
            const suffix = searchParams.toString();
            return postRaw(
              config,
              `resources/datasets/csv/create-raw${
                suffix !== '' ? `?${suffix}` : ''
              }`,
              p.file,
              withApiSchema(DatasetCsvCreateV20260601Schema.version),
              p.onUploadProgress
            );
          },
          createCsvPending: (p) => {
            const { id, ...body } = p;
            return post(
              config,
              `resources/datasets/csv/${enc(id)}`,
              body,
              withApiSchema(DatasetCsvCreatePendingV20260601Schema.version)
            );
          },
          promote: (p) => {
            const { id, ...body } = p;
            return post(
              config,
              `resources/datasets/${enc(id)}/promote`,
              body,
              withApiSchema(DatasetPromoteV20260601Schema.version)
            );
          },
          createManual: (p) => {
            const { id, ...body } = p;
            return post(
              config,
              `resources/datasets/manual/${enc(id)}`,
              body,
              withApiSchema(DatasetManualCreateV20260601Schema.version)
            );
          },
          createWarehouse: (p) => {
            const { id, ...body } = p;
            return post(
              config,
              `resources/datasets/warehouse/${enc(id)}`,
              body,
              withApiSchema(DatasetWarehouseCreateV20260601Schema.version)
            );
          },
          reseedWarehouse: (p) => {
            const { id, ...body } = p;
            return post(
              config,
              `resources/datasets/warehouse/${enc(id)}/reseed`,
              body,
              withApiSchema(DatasetWarehouseReseedV20260601Schema.version)
            );
          },
          detectWarehouse: (p) =>
            post(config, `resources/datasets/warehouse/${enc(p.id)}/detect`),
          flushWarehouse: (p) =>
            post(config, `resources/datasets/warehouse/${enc(p.id)}/flush`),
          importCsv: (p) =>
            postRaw(
              config,
              `resources/datasets/manual/${enc(
                p.id
              )}/import-csv?originalFilename=${encodeURIComponent(
                p.file.name
              )}`,
              p.file
            ),
          checkRowKeys: (p) => {
            const { id, ...body } = p;
            return post(
              config,
              `resources/datasets/${enc(id)}/row-key-check`,
              body,
              withApiSchema(DatasetRowKeyCheckV20260601Schema.version)
            );
          },
          uploadCsvBytes: (p) =>
            postRaw(
              config,
              `resources/datasets/csv/${enc(
                p.id
              )}/import-raw?originalFilename=${encodeURIComponent(
                p.file.name
              )}`,
              p.file,
              undefined,
              p.onUploadProgress
            ),
          uploadChatFile: (p) =>
            postRaw(
              config,
              `resources/chat-files/${enc(
                p.id
              )}/upload?chatId=${encodeURIComponent(
                p.chatId
              )}&filename=${encodeURIComponent(
                p.filename
              )}&mediaType=${encodeURIComponent(p.mediaType)}`,
              p.file,
              undefined,
              p.onUploadProgress
            ),
          artifactUrl: (p) =>
            get(
              config,
              `resources/datasets/${enc(p.id)}/${p.kind}/${enc(
                p.artifactId
              )}/url`
            ),
          listOperations: (p) =>
            get(config, `resources/datasets/${enc(p.id)}/operations`, {
              afterSequenceNumber: p.afterSequenceNumber,
            }),
          appendOperations: (p) =>
            post(
              config,
              `resources/datasets/${enc(p.id)}/operations/batch`,
              {
                baseArtifactId: p.baseArtifactId,
                operations: p.operations,
              },
              withApiSchema(DatasetOperationsBatchV20260601Schema.version)
            ),
          rowCount: (p) =>
            get(config, `resources/datasets/${enc(p.id)}/row-count`),
          updateSettings: (p) => {
            const { id, ...body } = p;
            return post(
              config,
              `resources/datasets/${enc(id)}/settings`,
              body,
              withApiSchema(DatasetSettingsV20260601Schema.version)
            );
          },
          updateColumnSettings: (p) => {
            const { id, ...body } = p;
            return post(
              config,
              `resources/datasets/${enc(id)}/column-settings`,
              body,
              withApiSchema(DatasetColumnSettingsV20260601Schema.version)
            );
          },
          archive: (p) =>
            post(config, `resources/datasets/${enc(p.id)}/archive`),
          detect: (p) => post(config, `resources/datasets/${enc(p.id)}/detect`),
        },

        workflow: {
          create: (p) => {
            const { id, ...bodyWithoutId } = p;
            return post(
              config,
              `resource/workflow/${enc(id)}`,
              bodyWithoutId,
              withApiSchema(WorkflowCreateV20260601Schema.version)
            );
          },
          update: (p) =>
            post(
              config,
              `resource/workflow/${enc(p.id)}/update`,
              {
                name: p.name,
                workflowStatus: p.workflowStatus,
                graph: p.graph,
                idempotencyKey: p.idempotencyKey,
              },
              withApiSchema(WorkflowUpdateV20260601Schema.version)
            ),
          delete: (p) => post(config, `resource/workflow/${enc(p.id)}/delete`),
          getScheduledAgentBundle: (p) =>
            get(
              config,
              `resource/workflow/${enc(p.workflowId)}/scheduled-agent`
            ),
          updateScheduledAgentRun: (p) =>
            post(
              config,
              `resource/workflow/${enc(p.id)}/scheduled-agent`,
              {
                name: p.name,
                schedule: p.schedule,
                timezone: p.timezone,
                agentId: p.agentId,
                prompt: p.prompt,
              },
              withApiSchema(WorkflowScheduledAgentUpdateV20260601Schema.version)
            ),
          runScheduledAgentNow: (p) =>
            post(config, `resource/workflow/${enc(p.id)}/scheduled-agent/run`),
          listRuns: (p) =>
            get(
              config,
              `resource/workflow/${enc(p.workflowId)}/runs`,
              {
                limit: p.limit,
                offset: p.offset,
                status: p.status,
                createdAfter: p.createdAfter,
                createdBefore: p.createdBefore,
                triggerType: p.triggerType,
              },
              withApiSchema(WorkflowListRunsV20260601Schema.version)
            ),
          runStats: (p) =>
            get(
              config,
              `resource/workflow/${enc(p.workflowId)}/runs/stats`,
              {
                status: p.status,
                createdAfter: p.createdAfter,
                createdBefore: p.createdBefore,
                triggerType: p.triggerType,
                bucket: p.bucket,
              },
              withApiSchema(WorkflowRunStatsV20260601Schema.version)
            ),
          getRun: (p) =>
            get(
              config,
              `resource/workflow/${enc(p.workflowId)}/runs/${enc(p.runId)}`
            ),
          getRunGraph: (p) =>
            get(
              config,
              `resource/workflow/${enc(p.workflowId)}/runs/${enc(
                p.runId
              )}/graph`
            ),
          getRunStepPayload: (p) =>
            get(
              config,
              `resource/workflow/${enc(p.workflowId)}/runs/${enc(
                p.runId
              )}/steps/${enc(p.nodeId)}/payload`
            ),
          cancelRun: (p) =>
            post(
              config,
              `resource/workflow/${enc(p.workflowId)}/runs/${enc(
                p.runId
              )}/cancel`,
              {},
              withApiSchema(WorkflowCancelRunV20260601Schema.version)
            ),
          retryRun: (p) =>
            post(
              config,
              `resource/workflow/${enc(p.workflowId)}/runs/${enc(
                p.runId
              )}/retry`,
              {},
              withApiSchema(WorkflowRetryRunV20260601Schema.version)
            ),
          clearDebounce: (p) =>
            post(
              config,
              `resource/workflow/${enc(p.workflowId)}/runs/${enc(
                p.runId
              )}/steps/${enc(p.nodeId)}/clear-debounce`,
              {},
              withApiSchema(WorkflowClearDebounceV20260601Schema.version)
            ),
          resumeRunStep: ({ workflowId, runId, nodeId, ...body }) =>
            post(
              config,
              `resource/workflow/${enc(workflowId)}/runs/${enc(
                runId
              )}/steps/${enc(nodeId)}/resume`,
              body,
              withApiSchema(WorkflowResumeStepV20260601Schema.version)
            ),
          listTriggers: (p) =>
            get(config, `resource/workflow/${enc(p.workflowId)}/triggers`),
          addTrigger: ({ workflowId, ...body }) =>
            post(
              config,
              `resource/workflow/${enc(workflowId)}/triggers`,
              body,
              withApiSchema(WorkflowAddTriggerV20260601Schema.version)
            ),
          updateTrigger: (p) =>
            post(
              config,
              `resource/workflow/${enc(p.workflowId)}/triggers/${enc(
                p.bindingId
              )}`,
              {
                triggerId: p.triggerId,
                name: p.name,
                schedule: p.schedule,
                timezone: p.timezone,
                bindingEnabled: p.bindingEnabled,
                cronRowEnabled: p.cronRowEnabled,
                webhookRowEnabled: p.webhookRowEnabled,
                eventRowEnabled: p.eventRowEnabled,
                providerKey: p.providerKey,
                verificationConfig: p.verificationConfig,
                eventKey: p.eventKey,
                eventConfig: p.eventConfig,
                endpointSecret: p.endpointSecret,
                eventName: p.eventName,
                priority: p.priority,
                defaultInput: p.defaultInput,
                runMode: p.runMode,
              },
              withApiSchema(WorkflowUpdateTriggerV20260601Schema.version)
            ),
          renameTrigger: (p) =>
            post(
              config,
              `resource/workflow/triggers/${enc(p.triggerId)}/rename`,
              { name: p.name },
              withApiSchema(WorkflowRenameTriggerV20260601Schema.version)
            ),
          getWebhookTriggerSettings: (p) =>
            get(
              config,
              `resource/workflow/triggers/webhook/${enc(p.triggerId)}`
            ),
          triggerNow: ({ triggerId, ...body }) =>
            post(
              config,
              `resource/workflow/triggers/${enc(triggerId)}/trigger-now`,
              body,
              withApiSchema(WorkflowTriggerNowV20260601Schema.version)
            ),
          datasetAutomations: (p) =>
            get(
              config,
              `resource/workflow/datasets/${enc(p.datasetId)}/automations`
            ),
          datasetAutomationOverlay: (p) =>
            get(
              config,
              `resource/workflow/datasets/${enc(
                p.datasetId
              )}/automation-overlay${
                p.artifactId === null || p.artifactId === undefined
                  ? ''
                  : `?artifactId=${enc(p.artifactId)}`
              }`
            ),
          cancelDatasetRunGroup: (p) =>
            post(
              config,
              `resource/workflow/datasets/${enc(
                p.datasetId
              )}/run-groups/cancel`,
              { groupId: p.groupId }
            ),
          collectDatasetRunGroupRows: (p) =>
            post(
              config,
              `resource/workflow/datasets/${enc(
                p.datasetId
              )}/run-groups/collect`,
              {
                groupId: p.groupId,
                triggerId: p.triggerId,
                rowKeys: p.rowKeys,
                ...(p.workflowIds === undefined
                  ? {}
                  : { workflowIds: p.workflowIds }),
              }
            ),
          startDatasetRunGroup: (p) =>
            post(
              config,
              `resource/workflow/datasets/${enc(p.datasetId)}/run-groups/start`,
              {
                groupId: p.groupId,
                triggerId: p.triggerId,
                ...(p.workflowIds === undefined
                  ? {}
                  : { workflowIds: p.workflowIds }),
              }
            ),
          datasetRunGroups: (p) =>
            get(
              config,
              `resource/workflow/datasets/${enc(p.datasetId)}/run-groups${
                p.activeOnly === true || p.limit !== undefined
                  ? `?${[
                      ...(p.activeOnly === true ? ['activeOnly=true'] : []),
                      ...(p.limit !== undefined ? [`limit=${p.limit}`] : []),
                    ].join('&')}`
                  : ''
              }`
            ),
          replayDataset: (p) =>
            post(
              config,
              `resource/workflow/datasets/${enc(p.datasetId)}/replay`,
              {
                groupId: p.groupId,
                ...(p.workflowIds !== undefined && {
                  workflowIds: p.workflowIds,
                }),
                onlyNeedsRun: p.onlyNeedsRun,
              }
            ),
          retryFailedDatasetRuns: (p) =>
            post(
              config,
              `resource/workflow/datasets/${enc(p.datasetId)}/retry-failed`,
              {
                ...(p.workflowIds !== undefined && {
                  workflowIds: p.workflowIds,
                }),
              }
            ),
          listEvents: () =>
            get(
              config,
              'resource/workflow/events',
              undefined,
              withApiSchema(WorkflowEventsV20260601Schema.version)
            ),
          draftTestRun: (p) =>
            post(
              config,
              `resource/workflow/${enc(p.workflowId)}/draft-test-run`,
              {
                graph: p.graph,
                runInput: p.runInput,
                name: p.name,
              },
              withApiSchema(WorkflowDraftTestRunV20260601Schema.version)
            ),
          trigger: (p) =>
            post(
              config,
              `resource/workflow/${enc(p.workflowId)}/trigger`,
              p.runInput !== undefined ? { runInput: p.runInput } : {},
              withApiSchema(WorkflowTriggerV20260601Schema.version)
            ),
          bindTrigger: (p) =>
            post(
              config,
              `resource/workflow/${enc(p.workflowId)}/triggers/${enc(
                p.triggerId
              )}/bind`,
              {
                priority: p.priority,
                defaultInput: p.defaultInput,
                enabled: p.enabled,
              },
              withApiSchema(WorkflowBindTriggerV20260601Schema.version)
            ),
          listWorkflowsForTrigger: (p) =>
            get(
              config,
              `resource/workflow/triggers/${enc(p.triggerId)}/workflows`
            ),
          runsByWorkflows: (p) =>
            post(
              config,
              'resource/workflow/runs-by-workflows',
              {
                workflowIds: p.workflowIds,
                createdAfter: p.createdAfter,
                createdBefore: p.createdBefore,
                limit: p.limit,
              },
              withApiSchema(WorkflowRunsByWorkflowsV20260601Schema.version)
            ),
          summariesByIds: (p) =>
            post(
              config,
              'resource/workflow/summaries',
              { workflowIds: p.workflowIds },
              withApiSchema(WorkflowSummariesV20260601Schema.version)
            ),
          triggerSummariesByIds: (p) =>
            post(
              config,
              'resource/workflow/trigger-summaries',
              { triggerIds: p.triggerIds },
              withApiSchema(WorkflowTriggerSummariesV20260601Schema.version)
            ),
        },

        folder: {
          create: (p) =>
            post(
              config,
              'resource/folder',
              p,
              withApiSchema(FolderCreateV20260601Schema.version)
            ),
          rename: (p) =>
            post(
              config,
              `resource/folder/${enc(p.folderId)}/rename`,
              { name: p.name },
              withApiSchema(FolderRenameV20260601Schema.version)
            ),
          move: (p) =>
            post(
              config,
              `resource/folder/${enc(p.folderId)}/move`,
              { newParentId: p.newParentId },
              withApiSchema(FolderMoveV20260601Schema.version)
            ),
          archive: (p) =>
            post(config, `resource/folder/${enc(p.folderId)}/archive`),
          assignToFolder: (p) =>
            post(
              config,
              `resource/folder/${enc(p.folderId)}/items/${enc(
                p.entityType
              )}/${enc(p.entityId)}/assign`
            ),
          unassignFromFolder: (p) =>
            post(
              config,
              `resource/folder/items/${enc(p.entityType)}/${enc(
                p.entityId
              )}/unassign`
            ),
          getDescendantFolderIds: (p) =>
            get(config, `resource/folder/${enc(p.folderId)}/descendants`),
          getFolderItems: (p) =>
            get(
              config,
              `resource/folder/${enc(p.folderId)}/items`,
              {
                folderScope: p.folderScope,
                entityType: p.entityType,
                itemTypes: p.itemTypes,
                search: p.search,
                sort: p.sort,
                sortDir: p.sortDir,
                limit: p.limit,
                offset: p.offset,
                unreadOnly: p.unreadOnly,
                triggerSources: p.triggerSources,
              },
              withApiSchema(FolderItemsV20260601Schema.version)
            ),
          getLibraryRoot: (p) =>
            get(
              config,
              'resource/folder/library-root',
              {
                ownership: p?.ownership,
                search: p?.search,
                sort: p?.sort,
                sortDir: p?.sortDir,
                limit: p?.limit,
                offset: p?.offset,
              },
              withApiSchema(FolderLibraryRootV20260601Schema.version)
            ),
          getUnfiledTotals: (p) =>
            get(
              config,
              p?.folderScope !== undefined
                ? `resource/folder/unfiled-totals/${enc(p.folderScope)}`
                : 'resource/folder/unfiled-totals'
            ),
          moveToFolder: (p) =>
            post(
              config,
              `resource/folder/${enc(p.folderId)}/items/${enc(p.type)}/${enc(
                p.id
              )}/move`
            ),
          getOrganizeDraft: (p) =>
            get(
              config,
              `resource/folder/organize/draft?conversationId=${enc(
                p.conversationId
              )}`
            ),
          createOrganizeDraft: (p) =>
            post(config, 'resource/folder/organize/draft', p),
          openOrganize: (p) => post(config, 'resource/folder/organize/open', p),
          addOrganizeDraftFolder: (p) =>
            post(config, 'resource/folder/organize/draft/folder', p),
          renameOrganizeDraftFolder: (p) =>
            post(config, 'resource/folder/organize/draft/folder/rename', p),
          removeOrganizeDraftFolder: (p) =>
            post(config, 'resource/folder/organize/draft/folder/remove', p),
          moveOrganizeDraftFolder: (p) =>
            post(config, 'resource/folder/organize/draft/folder/move', p),
          moveOrganizeDraftMember: (p) =>
            post(config, 'resource/folder/organize/draft/move', p),
          removeOrganizeDraftMember: (p) =>
            post(config, 'resource/folder/organize/draft/remove-member', p),
          commitOrganizeDraft: (p) =>
            post(config, 'resource/folder/organize/draft/commit', p),
        },
      },

      // -----------------------------------------------------------------------
      // playground (legacy root routes)
      // -----------------------------------------------------------------------
      playground: {
        chatUrl: rootUrl(config, 'playground/chat'),
        chatFetch: makeAuthenticatedFetch(config),
      },

      // -----------------------------------------------------------------------
      // devServer
      // -----------------------------------------------------------------------
      devServer: {
        manifests: {
          manifestMeta: () => get(config, 'dev-server/manifests/meta'),
          definition: (p) =>
            get(config, `dev-server/manifests/definitions/${enc(p.id)}`),
        },
      },
      workosWidget: {
        usersTableToken: () =>
          post(config, 'workos-widget/users-table-token', {}),
      },
    },

    // -------------------------------------------------------------------------
    // Untenanted
    // -------------------------------------------------------------------------
    u: {
      whoami: {
        get: () => get(config, 'whoami'),
      },
      onboarding: {
        submitOrgSetup: (p) =>
          post(
            config,
            'onboarding/org-setup',
            p,
            withApiSchema(OnboardingOrgSetupV20260601Schema.version)
          ),
        recordReferral: (p) =>
          post(
            config,
            'onboarding/referral',
            p,
            withApiSchema(OnboardingReferralV20260601Schema.version)
          ),
      },
      atcVisibility: {
        setAtcRunVisibility: (p) =>
          post(config, 'atc-visibility/set-atc-run-visibility', p),
        getOrgsByIds: (p) =>
          post(config, 'atc-visibility/orgs/by-ids', { orgIds: p.orgIds }),
        getRestateUrl: () => get(config, 'atc-visibility/restate/url'),
        getStatusSummary: (p) =>
          get(config, 'atc-visibility/status/summary', {
            timeInterval: p.timeInterval,
          }),
        getFailureTypeSummary: (p) =>
          get(config, 'atc-visibility/failures/summary', {
            timeInterval: p.timeInterval,
          }),
        getFailureTypeDetails: (p) =>
          get(
            config,
            `atc-visibility/failures/${enc(p.orgId)}/${enc(p.runId)}`
          ),
        getSagaInvocations: (p) =>
          get(config, 'atc-visibility/sagas', {
            timeInterval: p.timeInterval,
            ...(p.includeSagaType !== undefined
              ? { includeSagaType: p.includeSagaType }
              : {}),
            ...(p.excludeSagaTypes !== undefined &&
            p.excludeSagaTypes.length > 0
              ? { excludeSagaTypes: p.excludeSagaTypes.join(',') }
              : {}),
          }),
        getWebhookTriggers: (p) =>
          get(config, 'atc-visibility/webhook-triggers', {
            timeInterval: p.timeInterval,
          }),
        retrySagaInvocation: (p) =>
          post(config, `atc-visibility/sagas/${enc(p.invocationId)}/retry`),
        restartAtcFailuresAsNew: (p) =>
          post(config, 'atc-visibility/failures/restart', p),
        getInvocationSummary: (p) =>
          get(config, 'atc-visibility/invocations/summary', {
            timeInterval: p.timeInterval,
          }),
        getActiveInvocationsByOrg: () =>
          get(config, 'atc-visibility/invocations/active'),
        killOrgInvocations: (p) =>
          post(config, `atc-visibility/orgs/${enc(p.orgId)}/kill`),
        getStackFramesBreakdown: (p) =>
          get(config, 'atc-visibility/stack-frames/breakdown', {
            timeInterval: p.timeInterval,
          }),
        executeCustomQuery: (p) =>
          post(config, 'atc-visibility/queries/execute', p),
      },
      superAdminBilling: {
        addOrbCredit: (p) =>
          post(config, 'super-admin-billing/add-orb-credit', p),
        billEngineeringHours: (p) =>
          post(config, 'super-admin-billing/engineering-hours', p),
      },
      cli: {
        getDeviceCodeStatus: (p) =>
          rootGet(config, 'api/cli/device-code/status', {
            device_code: p.deviceCode,
          }),
        approve: (p) =>
          rootPost(
            config,
            'api/cli/approve',
            { device_code: p.deviceCode },
            withApiSchema(CliAuthApproveV20260601Schema.version)
          ),
        getStatus: () => rootGet(config, 'api/cli/status'),
      },
      transcribe: {
        audio: (p) => rootPostFormData(config, 'transcribe/audio', p.formData),
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Dev server client types (inlined from @cotera/api cotera-client.ts)
// ---------------------------------------------------------------------------

export type DevServerError = { errorType: 'NoDevServerFound' | 'OrgNotFound' };

export type ManifestCompilerError = {
  errorType: 'CompileError';
  stackTrace: string;
};

export type CoteraClientError =
  | { errorType: 'ParseError'; err: unknown }
  | DevServerError
  | ManifestCompilerError;

export type DevServerClient = CoteraApiV2['t']['devServer'];

async function localDevServerRequest<T>(
  orgId: string,
  route: string,
  input?: Record<string, unknown>
): Promise<Result<T, ApiErr>> {
  try {
    const response = await axios({
      url: `http://localhost:3000/${route}`,
      method: 'POST',
      data: { orgId, ...(input ?? {}) },
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-HTTP-Method-Override': 'GET',
      },
      validateStatus: () => true,
    });

    if (response.status >= 200 && response.status < 300) {
      return ok(response.data as T);
    }
    return err(parseApiErr(response.status, response.data));
  } catch (e) {
    const error = e as AxiosError;
    if (error.response?.status === 404) {
      return err({ t: 'client', err: { errorType: 'OrgNotFound' } });
    }
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      return err({ t: 'client', err: { errorType: 'NoDevServerFound' } });
    }
    throw e;
  }
}

/** Plain HTTP wrapper for the local manifest dev server on port 3000. */
export const mkLocalDevServer = (orgId: string): DevServerClient => ({
  manifests: {
    manifestMeta: (_p) =>
      localDevServerRequest<ManifestMeta>(orgId, 'manifests/manifest-meta'),
    definition: (p) =>
      localDevServerRequest<AST.RelFR | null>(orgId, 'manifests/definition', {
        id: p.id,
        version: p.version,
      }),
  },
});

// ---------------------------------------------------------------------------
// Backwards-compat aliases
// Files that imported TenantedClient from '@cotera/api' can now import from here.
// ---------------------------------------------------------------------------
export type TenantedClient = CoteraApiV2['t'];
export type CoteraClient = CoteraApiV2;
